// Admin screens: S02 Dashboard, S03 Runs list, S04-S07 New Run wizard,
// S08/S09 Run detail, S11 Customers, S14 Rates
import React, { useState as useStateAdmin, useEffect as useEffectAdmin, useMemo as useMemoAdmin } from 'react';
import * as XLSX from 'xlsx';
import { Icon, Pill, Button, Card, Money, Banner, Section, Select, Input } from './ui.jsx';
import { REPS, RATES, CLIENTS, RUNS, INVOICES, getRepTotals, sumBy } from './data.jsx';
import { api } from './api.js';
import { hasPermission } from './auth.jsx';

// ===================== S02 · Admin Dashboard =====================
export function ScreenAdminDashboard({ onNav, tenant, currentUser }) {
  const [runs, setRuns]           = useStateAdmin([]);
  const [invoices, setInvoices]   = useStateAdmin([]);
  const [customers, setCustomers] = useStateAdmin([]);
  const [reps, setReps]           = useStateAdmin([]);
  const [loading, setLoading]     = useStateAdmin(true);

  useEffectAdmin(() => {
    let alive = true;
    (async () => {
      try {
        const [rs, cs, us] = await Promise.all([
          api.runs.list(),
          api.customers.list(),
          api.users.list(),
        ]);
        if (!alive) return;
        setRuns(rs);
        setCustomers(cs);
        setReps(us.filter(u => u.role === 'rep' && u.active));
        // Load invoices for the most recent (current) run.
        const latest = rs[0];
        if (latest) {
          const invs = await api.invoices.listByRun(latest.id);
          if (!alive) return;
          setInvoices(invs);
        }
      } catch (err) { console.error('Dashboard load:', err); }
      finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, []);

  if (loading) return <div className="p-10 text-center text-neutral-500 text-[13px]">Loading dashboard…</div>;

  const now = new Date();
  const today = now.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });
  const greeting = now.getHours() < 12 ? 'Good morning' : now.getHours() < 18 ? 'Good afternoon' : 'Good evening';
  const firstName = (currentUser?.name || 'there').split(' ')[0];

  const currentRun   = runs[0] || null;
  const lastFinalized = runs.find(r => r.status === 'finalized') || null;
  const unassignedClients = customers.filter(c => !c.repUserId).length;
  const openAR = invoices.filter(i => !i.paid).reduce((a, i) => a + i.outstanding, 0);
  const totalBill = invoices.reduce((a, i) => a + i.totalBill, 0);
  const totalGP   = invoices.reduce((a, i) => a + i.grossProfit, 0);
  const totalCommission = invoices.filter(i => !i.excluded).reduce((a, i) => a + i.commission, 0);

  // Empty-state when no runs exist yet.
  if (runs.length === 0) {
    return (
      <div className="space-y-8">
        <div>
          <div className="text-[12px] text-neutral-500 uppercase tracking-[0.08em] font-semibold">{today}</div>
          <h1 className="text-[26px] font-bold text-neutral-900 tracking-[-0.015em] mt-0.5">{greeting}, {firstName}</h1>
          <div className="text-[14px] text-neutral-600 mt-1">
            Welcome to <b>{tenant?.name || 'your workspace'}</b>. Get started by creating your first commission run.
          </div>
        </div>

        <Card padding="p-10" className="text-center">
          <div className="w-16 h-16 rounded-full bg-primary-100 text-primary-700 flex items-center justify-center mx-auto mb-4">
            <Icon name="upload" size={26}/>
          </div>
          <div className="text-[20px] font-bold text-neutral-900">No commission runs yet</div>
          <div className="text-[14px] text-neutral-500 mt-2 max-w-[460px] mx-auto leading-relaxed">
            Create a run, upload your Transaction and AR files, assign your reps, and you'll have a payroll-ready commission summary in minutes.
          </div>
          <div className="mt-6 flex items-center justify-center gap-2">
            {hasPermission(currentUser, 'runs', 'create') && (
              <Button variant="primary" size="md" icon="plus" onClick={() => onNav('s04')}>Start a new run</Button>
            )}
            {hasPermission(currentUser, 'users', 'invite') && (
              <Button variant="ghost" size="md" onClick={() => onNav('s22')}>Invite your team first</Button>
            )}
          </div>
        </Card>

        <div className="grid grid-cols-3 gap-4">
          {[
            { icon: 'reps',      label: 'Sales reps',   v: reps.length,      cta: 'Manage users', nav: 's22' },
            { icon: 'rates',     label: 'Rates',        v: tenant?.rates ? `Y1 ${(tenant.rates.y1*100).toFixed(0)}% · Y2+ ${(tenant.rates.y2*100).toFixed(0)}%` : '—', cta: 'Edit rates', nav: 's14' },
            { icon: 'customers', label: 'Customers',    v: customers.length, cta: 'View', nav: 's11' },
          ].map(q => (
            <Card key={q.label} padding="p-5">
              <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.06em] text-neutral-500 mb-2"><Icon name={q.icon} size={13}/> {q.label}</div>
              <div className="text-[20px] font-bold text-neutral-900">{q.v}</div>
              <div className="mt-3"><Button size="sm" variant="tertiary" icon="arrow" onClick={() => onNav(q.nav)}>{q.cta}</Button></div>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  // Compute real rep leaderboard from DB invoices.
  const repStats = reps.map(rep => {
    const mine = invoices.filter(i => i.repUserId === rep.id && !i.excluded);
    return {
      rep,
      count:     mine.length,
      gp:        mine.reduce((a, i) => a + i.grossProfit, 0),
      commission:mine.reduce((a, i) => a + i.commission, 0),
    };
  }).filter(r => r.count > 0).sort((a, b) => b.commission - a.commission);
  const maxCommission = Math.max(1, ...repStats.map(r => r.commission));

  // Real activity from runs/invoices (no mock).
  const activity = [];
  runs.forEach(r => {
    if (r.finalizedAt)   activity.push({ t: r.finalizedAt.slice(0, 10), actor: 'Admin', action: 'finalized',  obj: r.period });
    if (r.approvedAt)    activity.push({ t: r.approvedAt.slice(0, 10),  actor: 'Admin', action: 'approved',   obj: r.period });
    activity.push({ t: r.createdAt.slice(0, 10), actor: 'Admin', action: 'created', obj: `Run · ${r.period}` });
  });
  activity.sort((a, b) => (b.t || '').localeCompare(a.t || ''));

  return (
    <div className="space-y-8">
      <div>
        <div className="text-[12px] text-neutral-500 uppercase tracking-[0.08em] font-semibold">{today}</div>
        <h1 className="text-[26px] font-bold text-neutral-900 tracking-[-0.015em] mt-0.5">{greeting}, {firstName}</h1>
        <div className="text-[14px] text-neutral-600 mt-1">
          {currentRun.status === 'draft'
            ? <>One run in progress. {unassignedClients > 0 ? <>Resolve <b>{unassignedClients}</b> unassigned clients before calculating.</> : 'All clients assigned — ready to calculate.'}</>
            : currentRun.status === 'approved'
              ? <>Most recent run <b>approved</b>. Finalize when payroll is ready.</>
              : <>Most recent run is <b>finalized</b>.</>}
        </div>
      </div>

      <div className="grid grid-cols-4 gap-4">
        <Card hover onClick={() => onNav('s08')} padding="p-5" className="border-t-[3px] border-t-primary-700">
          <div className="flex items-center justify-between">
            <Pill tone={currentRun.status === 'finalized' ? 'finalized' : currentRun.status === 'approved' ? 'paid' : 'draft'}>{currentRun.status}</Pill>
            <span className="text-[11px] text-neutral-500 font-mono">{currentRun.id.slice(0, 8)}</span>
          </div>
          <div className="mt-3 text-[11px] font-semibold uppercase tracking-[0.06em] text-neutral-500">Current run</div>
          <div className="mt-1 text-[20px] font-semibold text-neutral-900 tracking-[-0.01em]">{currentRun.period}</div>
          <div className="text-[12px] text-neutral-500 mt-0.5">
            {invoices.length.toLocaleString()} invoices · {customers.length} clients
          </div>
          {unassignedClients > 0 && (
            <div className="mt-3 flex items-center gap-2 text-[12px] text-amber-800 bg-amber-50 border border-amber-200 px-2 py-1 rounded">
              <Icon name="warn" size={12}/> <span>{unassignedClients} client{unassignedClients === 1 ? '' : 's'} need a rep</span>
            </div>
          )}
          <div className="mt-4 flex gap-2"><Button size="sm" variant="primary" icon="arrow" onClick={() => onNav('s08')}>Open run</Button></div>
        </Card>

        <Card padding="p-5">
          <div className="text-[11px] font-semibold uppercase tracking-[0.06em] text-neutral-500">Last finalized</div>
          {lastFinalized ? (
            <>
              <div className="mt-2 text-[22px] font-bold font-mono tabular-nums text-neutral-900 leading-none">
                ${(lastFinalized.totals?.commission || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </div>
              <div className="text-[12px] text-neutral-500 mt-1.5">{lastFinalized.period}</div>
            </>
          ) : (
            <>
              <div className="mt-2 text-[22px] font-bold font-mono tabular-nums text-neutral-400 leading-none">—</div>
              <div className="text-[12px] text-neutral-500 mt-1.5">No runs finalized yet</div>
            </>
          )}
        </Card>

        <Card padding="p-5" className={unassignedClients > 0 ? 'border-amber-300' : ''}>
          <div className="flex items-center justify-between">
            <div className="text-[11px] font-semibold uppercase tracking-[0.06em] text-neutral-500">Unassigned clients</div>
            {unassignedClients > 0 && <Pill tone="warn">needs you</Pill>}
          </div>
          <div className={`mt-2 text-[30px] font-bold font-mono tabular-nums leading-none ${unassignedClients > 0 ? 'text-amber-700' : 'text-neutral-900'}`}>
            {unassignedClients}
          </div>
          <div className="text-[12px] text-neutral-500 mt-1.5">{unassignedClients === 0 ? 'All clients assigned' : 'Blocking commission'}</div>
          {unassignedClients > 0 && <div className="mt-4"><Button size="sm" variant="secondary" icon="arrow" onClick={() => onNav('s11')}>Resolve</Button></div>}
        </Card>

        <Card padding="p-5">
          <div className="text-[11px] font-semibold uppercase tracking-[0.06em] text-neutral-500">Open AR</div>
          <div className="mt-2 text-[22px] font-bold font-mono tabular-nums text-neutral-900 leading-none">
            ${openAR.toLocaleString('en-US', { maximumFractionDigits: 0 })}
          </div>
          <div className="text-[12px] text-neutral-500 mt-1.5">{invoices.filter(i => !i.paid).length} invoices outstanding</div>
        </Card>
      </div>

      <div className="grid grid-cols-3 gap-6">
        <div className="col-span-2 space-y-3">
          <Section title="This run at a glance" sub={currentRun.period}>
            <Card padding="p-0">
              <div className="grid grid-cols-3 divide-x divide-neutral-200">
                <div className="p-5">
                  <div className="text-[11px] font-semibold uppercase tracking-[0.06em] text-neutral-500">Total Bill</div>
                  <div className="mt-1 text-[22px] font-bold font-mono tabular-nums text-neutral-900">${totalBill.toLocaleString('en-US', { maximumFractionDigits: 0 })}</div>
                </div>
                <div className="p-5">
                  <div className="text-[11px] font-semibold uppercase tracking-[0.06em] text-neutral-500">Gross Profit</div>
                  <div className="mt-1 text-[22px] font-bold font-mono tabular-nums text-neutral-900">${totalGP.toLocaleString('en-US', { maximumFractionDigits: 0 })}</div>
                </div>
                <div className="p-5">
                  <div className="text-[11px] font-semibold uppercase tracking-[0.06em] text-neutral-500">Commission</div>
                  <div className="mt-1 text-[22px] font-bold font-mono tabular-nums text-neutral-900">${totalCommission.toLocaleString('en-US', { maximumFractionDigits: 0 })}</div>
                </div>
              </div>
              <div className="border-t border-neutral-200 px-5 py-3 bg-canvas/60 flex items-center justify-between text-[12px] text-neutral-600">
                <span>Rates: ACA {(tenant?.rates?.aca * 100 || 1.8).toFixed(2)}% · SICK {(tenant?.rates?.sick * 100 || 1.2).toFixed(2)}% · Y1 {(tenant?.rates?.y1 * 100 || 5).toFixed(0)}% · Y2+ {(tenant?.rates?.y2 * 100 || 7).toFixed(0)}%</span>
                <button onClick={() => onNav('s23')} className="text-primary-700 hover:text-primary-900 font-medium">Edit commission plan →</button>
              </div>
            </Card>
          </Section>

          <Section title="Rep leaderboard" sub={`${repStats.length} rep${repStats.length === 1 ? '' : 's'} with invoices in this run`}>
            <Card padding="p-0">
              <table className="w-full text-[13px]">
                <thead>
                  <tr className="text-left text-[11px] uppercase tracking-[0.06em] text-neutral-500 border-b border-neutral-200">
                    <th className="px-4 py-2.5 font-semibold">Rep</th>
                    <th className="px-4 py-2.5 font-semibold text-right">Invoices</th>
                    <th className="px-4 py-2.5 font-semibold text-right">Gross Profit</th>
                    <th className="px-4 py-2.5 font-semibold text-right">Commission</th>
                    <th className="px-4 py-2.5 font-semibold w-[32%]"/>
                  </tr>
                </thead>
                <tbody className="divide-y divide-neutral-200">
                  {repStats.length === 0 && (
                    <tr><td colSpan={5} className="px-4 py-8 text-center text-[13px] text-neutral-500">
                      No reps have invoices in this run yet. Assign customers to reps to see the leaderboard.
                    </td></tr>
                  )}
                  {repStats.map(r => (
                    <tr key={r.rep.id} className="hover:bg-primary-100/30">
                      <td className="px-4 py-2.5">
                        <div className="flex items-center gap-2.5">
                          <div className="w-6 h-6 rounded-full bg-neutral-100 text-neutral-700 flex items-center justify-center text-[10px] font-bold">{r.rep.name.split(' ').map(s=>s[0]).join('')}</div>
                          <span className="font-medium text-neutral-900">{r.rep.name}</span>
                        </div>
                      </td>
                      <td className="px-4 py-2.5 text-right font-mono tabular-nums text-neutral-700">{r.count}</td>
                      <td className="px-4 py-2.5 text-right"><Money value={r.gp} className="text-neutral-700"/></td>
                      <td className="px-4 py-2.5 text-right"><Money value={r.commission} className="font-semibold text-neutral-900"/></td>
                      <td className="px-4 py-2.5">
                        <div className="h-1.5 rounded-full bg-neutral-100 overflow-hidden">
                          <div className="h-full bg-primary-700" style={{ width: `${(r.commission / maxCommission) * 100}%` }}/>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Card>
          </Section>
        </div>

        <div className="col-span-1">
          <Section title="Recent activity">
            <Card padding="p-0">
              {activity.length === 0 ? (
                <div className="p-6 text-center text-[13px] text-neutral-500">Activity will appear here as you create and finalize runs.</div>
              ) : (
                <ul className="divide-y divide-neutral-200">
                  {activity.slice(0, 8).map((a, i) => (
                    <li key={i} className="px-4 py-3 hover:bg-canvas text-[13px]">
                      <div className="flex items-baseline justify-between">
                        <span className="font-medium text-neutral-900">{a.actor}</span>
                        <span className="text-[11px] text-neutral-500 font-mono">{a.t}</span>
                      </div>
                      <div className="text-neutral-700 mt-0.5">
                        {a.action} <span className="text-neutral-900">{a.obj}</span>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </Card>
          </Section>
        </div>
      </div>
    </div>
  );
}

// ===================== S03 · Runs list =====================
export function ScreenRunsList({ onNav, currentUser }) {
  const [runs, setRuns] = useStateAdmin([]);
  const [loading, setLoading] = useStateAdmin(true);

  useEffectAdmin(() => {
    api.runs.list()
      .then(setRuns)
      .catch(err => console.error('Failed to load runs:', err))
      .finally(() => setLoading(false));
  }, []);

  const drafts = runs.filter(r => r.status === 'draft').length;
  const finalized = runs.length - drafts;

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between">
        <div>
          <h1 className="text-[24px] font-bold text-neutral-900 tracking-[-0.01em]">Commission runs</h1>
          <div className="text-[13px] text-neutral-500 mt-1">
            {runs.length} run{runs.length === 1 ? '' : 's'} · {drafts} draft · {finalized} finalized
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Select size="md" value="2026" onChange={() => {}} options={[{value:'2026',label:'2026'},{value:'2025',label:'2025'}]}/>
          <Select size="md" value="all" onChange={() => {}} options={[{value:'all',label:'All statuses'},{value:'draft',label:'Draft'},{value:'final',label:'Finalized'}]}/>
          {hasPermission(currentUser, 'runs', 'create') && (
            <Button variant="primary" icon="plus" onClick={() => onNav('s04')}>New run</Button>
          )}
        </div>
      </div>

      <Card padding="p-0">
        <table className="w-full text-[13px]">
          <thead>
            <tr className="text-left text-[11px] uppercase tracking-[0.06em] text-neutral-500 bg-canvas/60 border-b border-neutral-200">
              <th className="px-4 py-2.5 font-semibold">Period</th>
              <th className="px-4 py-2.5 font-semibold">Status</th>
              <th className="px-4 py-2.5 font-semibold text-right">Total commission</th>
              <th className="px-4 py-2.5 font-semibold">Finalized</th>
              <th className="px-4 py-2.5 font-semibold">By</th>
              <th className="px-4 py-2.5 w-8"/>
            </tr>
          </thead>
          <tbody className="divide-y divide-neutral-200">
            {loading && (
              <tr><td colSpan={6} className="px-4 py-10 text-center text-[13px] text-neutral-500">Loading runs…</td></tr>
            )}
            {!loading && runs.length === 0 && (
              <tr><td colSpan={6} className="px-4 py-10 text-center text-[13px] text-neutral-500">
                No runs yet. Click <b>New run</b> to start one.
              </td></tr>
            )}
            {runs.map(r => (
              <tr key={r.id} onClick={() => {
                // Pass the clicked run's id via URL so the detail screen
                // loads THAT run, not "the latest".
                window.history.pushState({}, '', `/commission-runs/detail?id=${r.id}`);
                window.dispatchEvent(new PopStateEvent('popstate'));
              }} className="hover:bg-primary-100/30 cursor-pointer">
                <td className="px-4 py-3">
                  <div className="font-semibold text-neutral-900">{r.period}</div>
                  <div className="text-[11px] text-neutral-500 font-mono">{r.id.slice(0, 8)}</div>
                </td>
                <td className="px-4 py-3"><Pill tone={r.status === 'draft' ? 'draft' : 'finalized'}>{r.status}</Pill></td>
                <td className="px-4 py-3 text-right"><Money value={r.totals?.commission ?? null} className="font-semibold"/></td>
                <td className="px-4 py-3 text-neutral-700">{r.finalizedAt?.slice(0, 10) || '—'}</td>
                <td className="px-4 py-3 text-neutral-700">{r.finalizedBy ? 'Admin' : '—'}</td>
                <td className="px-4 py-3 text-right text-neutral-400"><Icon name="chevron" size={14}/></td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </div>
  );
}

// ===================== Shared wizard stepper =====================
export function WizardStepper({ step, onStep }) {
  const steps = [
    { id: 1, label: 'Setup' },
    { id: 2, label: 'Upload Txn' },
    { id: 3, label: 'Upload Aging' },
    { id: 4, label: 'Assign' },
    { id: 5, label: 'Review' },
  ];
  return (
    <div className="flex items-center gap-2 mb-6">
      {steps.map((s, i) => {
        const done = s.id < step;
        const active = s.id === step;
        return (
          <React.Fragment key={s.id}>
            <button onClick={() => onStep && done && onStep(s.id)}
              className={`flex items-center gap-2 h-9 px-3 rounded-full border text-[12px] font-medium
                ${active ? 'border-primary-700 bg-primary-100 text-primary-900' :
                  done ? 'border-emerald-300 bg-emerald-50 text-emerald-800' :
                  'border-neutral-200 bg-white text-neutral-500'}`}>
              <span className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold
                ${active ? 'bg-primary-700 text-white' : done ? 'bg-emerald-600 text-white' : 'bg-neutral-200 text-neutral-600'}`}>
                {done ? <Icon name="check" size={11}/> : s.id}
              </span>
              {s.label}
            </button>
            {i < steps.length - 1 && <div className="w-6 h-px bg-neutral-200"/>}
          </React.Fragment>
        );
      })}
    </div>
  );
}

// ===================== S04 · New run setup =====================
// Small read-only card on the new-run setup screen that lists every
// commission plan in the tenant with its actual rates, so the admin sees
// what's about to drive the calculation. Replaces the old hardcoded
// "Rate snapshot" card that displayed fake static numbers.
function PlansInEffectCard({ onNav }) {
  const [plans, setPlans]     = useStateAdmin([]);
  const [loading, setLoading] = useStateAdmin(true);

  useEffectAdmin(() => {
    let alive = true;
    api.commissionPlans.list()
      .then(list => { if (alive) setPlans(list); })
      .catch(err => console.warn('plans load:', err))
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  if (loading) {
    return <div className="rounded-md border border-neutral-200 p-4 text-[12px] text-neutral-500">Loading plans…</div>;
  }

  if (plans.length === 0) {
    return (
      <div className="rounded-md border border-amber-200 bg-amber-50 p-4 text-[12.5px] text-amber-900">
        No commission plans found.{' '}
        <button onClick={() => onNav('s14')} className="font-semibold underline">Create one first</button>{' '}
        — every rep needs a plan to calculate commission.
      </div>
    );
  }

  // Format a deduction list ("ACA 1.8% · SICK 1.2%") or "—" if none.
  const fmtDeductions = (ds) => {
    if (!ds || ds.length === 0) return '—';
    return ds.map(d => `${d.label || d.field || 'fee'} ${(Number(d.rate) * 100).toFixed(2)}%`).join(' · ');
  };
  // Format the tier ladder for inline display.
  const fmtTiers = (ts) => {
    if (!ts || ts.length === 0) return '—';
    return ts.map(t => `${t.label || '?'} ${(Number(t.rate) * 100).toFixed(2)}%`).join(' · ');
  };

  return (
    <div className="space-y-2">
      {plans.map(p => (
        <div key={p.id} className="rounded-md border border-neutral-200 p-3">
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <div className="text-[13px] font-semibold text-neutral-900">{p.name}</div>
                {p.isDefault && <Pill tone="brand">Default</Pill>}
                <Pill tone={p.paymentBasis === 'cash' ? 'warn' : 'neutral'}>
                  {p.paymentBasis === 'cash' ? 'Hold until paid' : 'Pay on every invoice'}
                </Pill>
              </div>
              <div className="mt-1.5 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1 text-[11.5px] font-mono">
                <div><span className="text-neutral-500">Deductions:</span> <span className="text-neutral-900">{fmtDeductions(p.deductions)}</span></div>
                <div><span className="text-neutral-500">Tiers:</span> <span className="text-neutral-900">{fmtTiers(p.tiers)}</span></div>
              </div>
            </div>
          </div>
        </div>
      ))}
      <div className="text-[11px] text-neutral-500 pt-1">
        Need to change anything?{' '}
        <button onClick={() => onNav('s14')} className="text-primary-700 hover:text-primary-900 font-medium">
          Manage commission plans →
        </button>
      </div>
    </div>
  );
}

export function ScreenNewRunSetup({ onNav, wizard, setWizard, tenant, currentUser }) {
  // Default the month picker to the last COMPLETED calendar month. Today is
  // typically a few days into the new month when payroll runs, so this is
  // the right default 95% of the time.
  const lastCompletedMonth = (() => {
    const now = new Date();
    const y = now.getUTCFullYear(); const m = now.getUTCMonth(); // 0-indexed
    const target = new Date(Date.UTC(y, m - 1, 1));
    return `${target.getUTCFullYear()}-${String(target.getUTCMonth() + 1).padStart(2, '0')}`;
  })();

  const [period, setPeriod]       = useStateAdmin(wizard.period || 'full');
  const [monthValue, setMonthValue] = useStateAdmin(wizard.monthValue || lastCompletedMonth);
  // Custom range — default to last 30 days if not previously set.
  const today = new Date().toISOString().slice(0, 10);
  const thirtyAgo = new Date(Date.now() - 30 * 86400_000).toISOString().slice(0, 10);
  const [customStart, setCustomStart] = useStateAdmin(wizard.customStart || thirtyAgo);
  const [customEnd, setCustomEnd]     = useStateAdmin(wizard.customEnd   || today);

  // The run NAME is what the rep sees on their dashboard. Auto-derive from
  // the period choice so admins don't have to retype it, but let them edit.
  const [name, setName]       = useStateAdmin(wizard.name || '');
  const [nameTouched, setNameTouched] = useStateAdmin(false);
  const derivedName = period === 'full'
    ? fmtMonthLong(monthValue)
    : `${fmtDateShort(customStart)} – ${fmtDateShort(customEnd)}`;
  // If the admin hasn't manually edited the name, keep it in sync with the period.
  React.useEffect(() => {
    if (!nameTouched) setName(derivedName);
  }, [derivedName, nameTouched]);

  const [snapshot, setSnapshot] = useStateAdmin(wizard.snapshot || 'current');
  const [busy, setBusy] = useStateAdmin(false);
  const [error, setError] = useStateAdmin(null);

  // Resolve the period to actual start/end dates.
  const { periodStart, periodEnd } = (() => {
    if (period === 'full') {
      const { start, end } = monthBounds(monthValue);
      return { periodStart: start, periodEnd: end };
    }
    return { periodStart: customStart, periodEnd: customEnd };
  })();

  // Create the run row in Supabase when the user clicks Next. We persist at
  // step 1 instead of the end of the wizard so the upload wizard can write
  // invoices against a real run_id instead of in-memory state.
  // We stamp `created_by` so the approval flow can enforce approver != creator.
  const submit = async () => {
    setError(null);
    if (period === 'custom' && new Date(customStart) > new Date(customEnd)) {
      setError('Start date must be on or before end date.'); return;
    }
    setBusy(true);
    try {
      // Snapshot every commission plan into the run's rate_snapshot jsonb at
      // create time. The calculate() engine still queries live plans (so
      // last-second edits work), but having the snapshot means we can show
      // the rates that WERE in effect when the run was created, even if the
      // plans are edited later. Falls back to tenant.rates for backward compat.
      let rateSnapshot;
      try {
        const plans = await api.commissionPlans.list();
        rateSnapshot = { snapshotAt: new Date().toISOString(), plans };
      } catch (e) {
        console.warn('Could not snapshot plans, falling back to tenant.rates:', e);
        rateSnapshot = tenant?.rates || { aca: 0.018, sick: 0.012, y1: 0.05, y2: 0.07 };
      }
      const run = await api.runs.create({
        tenantId: tenant.id,
        period: name || derivedName,
        periodStart,
        periodEnd,
        status: 'draft',
        rateSnapshot,
        totals: {},
        createdBy: currentUser?.id || null,
      });
      setWizard({ ...wizard, name: name || derivedName, period, monthValue, customStart, customEnd, snapshot, run });
      onNav('s05');
    } catch (err) {
      setError(err.message || 'Could not create run.');
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="max-w-[780px] mx-auto">
      <div className="mb-2 text-[12px] text-neutral-500 font-semibold uppercase tracking-[0.06em]">New commission run</div>
      <h1 className="text-[24px] font-bold text-neutral-900 tracking-[-0.01em] mb-6">Set up the period</h1>
      <WizardStepper step={1}/>
      <Card className="space-y-6">
        <div>
          <label className="block text-[12px] font-semibold text-neutral-700 mb-1.5">Period</label>
          <div className="grid grid-cols-2 gap-3">
            <label className={`cursor-pointer rounded-md border p-4 flex gap-3 ${period === 'full' ? 'border-primary-700 bg-primary-100/60' : 'border-neutral-200 hover:border-neutral-300'}`}>
              <input type="radio" checked={period === 'full'} onChange={() => setPeriod('full')} className="mt-0.5"/>
              <div className="flex-1">
                <div className="text-[13px] font-semibold text-neutral-900">Full month</div>
                <div className="text-[11.5px] text-neutral-500 mt-0.5">A whole calendar month — standard for monthly payroll cycles.</div>
                {period === 'full' && (
                  <div className="mt-3" onClick={e => e.stopPropagation()}>
                    <input type="month" value={monthValue} onChange={e => setMonthValue(e.target.value)}
                      className="h-9 px-2.5 text-[13px] rounded-md border border-neutral-300 bg-white focus:outline-none focus:ring-2 focus:ring-primary-200 focus:border-primary-700"/>
                    <div className="text-[11px] text-neutral-500 mt-1.5 font-mono">
                      {fmtDateShort(monthBounds(monthValue).start)} – {fmtDateShort(monthBounds(monthValue).end)}
                    </div>
                  </div>
                )}
              </div>
            </label>
            <label className={`cursor-pointer rounded-md border p-4 flex gap-3 ${period === 'custom' ? 'border-primary-700 bg-primary-100/60' : 'border-neutral-200 hover:border-neutral-300'}`}>
              <input type="radio" checked={period === 'custom'} onChange={() => setPeriod('custom')} className="mt-0.5"/>
              <div className="flex-1">
                <div className="text-[13px] font-semibold text-neutral-900">Custom range</div>
                <div className="text-[11.5px] text-neutral-500 mt-0.5">Biweekly cycles, mid-month payroll, or one-off true-ups.</div>
                {period === 'custom' && (
                  <div className="mt-3 space-y-2" onClick={e => e.stopPropagation()}>
                    <div className="flex items-center gap-2">
                      <div className="flex-1">
                        <div className="text-[10.5px] font-semibold uppercase tracking-[0.06em] text-neutral-500 mb-1">Start</div>
                        <input type="date" value={customStart} onChange={e => setCustomStart(e.target.value)}
                          className="w-full h-9 px-2.5 text-[13px] rounded-md border border-neutral-300 bg-white focus:outline-none focus:ring-2 focus:ring-primary-200 focus:border-primary-700"/>
                      </div>
                      <div className="flex-1">
                        <div className="text-[10.5px] font-semibold uppercase tracking-[0.06em] text-neutral-500 mb-1">End</div>
                        <input type="date" value={customEnd} onChange={e => setCustomEnd(e.target.value)}
                          className="w-full h-9 px-2.5 text-[13px] rounded-md border border-neutral-300 bg-white focus:outline-none focus:ring-2 focus:ring-primary-200 focus:border-primary-700"/>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </label>
          </div>
        </div>

        <div>
          <label className="block text-[12px] font-semibold text-neutral-700 mb-1.5">Run name</label>
          <input
            value={name}
            onChange={e => { setName(e.target.value); setNameTouched(true); }}
            placeholder={derivedName}
            className="w-full h-10 px-3 text-[14px] rounded-md border border-neutral-300 focus:outline-none focus:ring-2 focus:ring-primary-200 focus:border-primary-700"/>
          <div className="text-[11px] text-neutral-500 mt-1">
            Auto-derived from the period — edit if you want a custom label.
            Used as the identifier in exports and audit logs.
          </div>
        </div>

        <div>
          <label className="block text-[12px] font-semibold text-neutral-700 mb-1.5">Plans in effect for this run</label>
          <div className="text-[11.5px] text-neutral-500 mb-3 leading-relaxed">
            Each rep earns commission per their assigned plan. The default plan is used for any rep
            without a specific assignment. Plans and their rates are snapshotted onto the run at
            create time, so changing a plan later won't retroactively change this run's math.
          </div>
          <PlansInEffectCard onNav={onNav}/>
        </div>
      </Card>
      {error && <div className="mt-3 text-[12px] text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2">{error}</div>}
      <div className="mt-5 flex items-center justify-between">
        <Button variant="ghost" onClick={() => onNav('s03')}>Cancel</Button>
        <Button variant="primary" icon="arrow" disabled={busy || !tenant} onClick={submit}>
          {busy ? 'Creating run…' : 'Next: Upload transaction file'}
        </Button>
      </div>
    </div>
  );
}

// ===================== File parsing helpers =====================
export function parseSpreadsheet(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        // cellDates: true makes SheetJS parse Excel date cells as real JS Date
        // objects instead of raw serial numbers (45716 → "2026-03-01").
        const wb = XLSX.read(e.target.result, { type: 'array', cellDates: true });
        const sheet = wb.Sheets[wb.SheetNames[0]];
        const rows = XLSX.utils.sheet_to_json(sheet, { defval: null });
        resolve(rows);
      } catch (err) { reject(err); }
    };
    reader.onerror = reject;
    reader.readAsArrayBuffer(file);
  });
}
// AR files from Sage are often HTML disguised as .xls. Detect and parse both paths.
export function parseARFile(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const buf = e.target.result;
      const head = new TextDecoder('utf-8').decode(buf.slice(0, 200)).toLowerCase();
      const isHtml = head.includes('<html') || head.includes('<table') || head.includes('<!doctype html');
      try {
        if (isHtml) {
          const text = new TextDecoder('utf-8').decode(buf);
          const doc = new DOMParser().parseFromString(text, 'text/html');
          const tables = doc.querySelectorAll('table');
          // Pick the largest table (the real one, not the metadata table)
          let best = null, bestRows = 0;
          tables.forEach(tbl => {
            const r = tbl.querySelectorAll('tr').length;
            if (r > bestRows) { best = tbl; bestRows = r; }
          });
          if (!best) return reject(new Error('No table found in HTML'));
          const trs = Array.from(best.querySelectorAll('tr'));
          // Find header row: first row with >=5 non-empty cells that aren't numbers
          let headerIdx = 0;
          for (let i = 0; i < Math.min(8, trs.length); i++) {
            const cells = Array.from(trs[i].querySelectorAll('th,td')).map(c => c.textContent.trim());
            const nonEmpty = cells.filter(Boolean);
            const hasLabel = nonEmpty.some(c => /customer|document|invoice|total|aged|date/i.test(c));
            if (hasLabel && nonEmpty.length >= 5) { headerIdx = i; break; }
          }
          const headers = Array.from(trs[headerIdx].querySelectorAll('th,td')).map(c => c.textContent.trim());
          const rows = [];
          let lastCustomer = null, lastCustomerId = null;
          for (let i = headerIdx + 1; i < trs.length; i++) {
            const cells = Array.from(trs[i].querySelectorAll('th,td')).map(c => c.textContent.trim());
            if (!cells.some(Boolean)) continue;
            const row = {};
            headers.forEach((h, j) => row[h] = cells[j] ?? '');
            // Skip "Total for X" subtotal rows
            const firstCol = row[headers[0]] || '';
            if (/^Total for /i.test(firstCol) || /^Grand total/i.test(firstCol)) continue;
            // Forward-fill customer name / ID
            if (row['Customer name'] || row[headers[0]]) {
              const nm = row['Customer name'] || row[headers[0]];
              if (nm && nm.trim()) lastCustomer = nm.trim();
            } else if (lastCustomer) row['Customer name'] = lastCustomer;
            const idCol = Object.keys(row).find(k => /customer\s*id/i.test(k));
            if (idCol) {
              if (row[idCol] && row[idCol].trim()) lastCustomerId = row[idCol].trim();
              else if (lastCustomerId) row[idCol] = lastCustomerId;
            }
            rows.push(row);
          }
          resolve(rows);
        } else {
          const wb = XLSX.read(buf, { type: 'array' });
          const sheet = wb.Sheets[wb.SheetNames[0]];
          resolve(XLSX.utils.sheet_to_json(sheet, { defval: null }));
        }
      } catch (err) { reject(err); }
    };
    reader.onerror = reject;
    reader.readAsArrayBuffer(file);
  });
}
/**
 * Carryover engine — Phase 4 (Option B, explicit lines).
 *
 * Called after AR has been applied to the current run. Walks every invoice
 * across the tenant that is currently held_for_collection=true and has
 * NOT yet been carried over. For any that don't appear in the current AR
 * file (i.e. the customer paid since the source run was calculated):
 *   - Creates a NEW invoice row in `currentRunId` with is_carryover=true,
 *     source_invoice_id pointing back, and commission rebuilt from the
 *     source's stored net × rate (preserving the original tier choice).
 *   - Marks the source invoice carried_over=true so it isn't picked up
 *     on a re-run.
 *
 * `currentArByKey` is the Map<branchKey, {outstanding,ageDays}> built by
 * the AR parser. Keys in this Map are invoices that are STILL unpaid;
 * anything not in this Map is paid.
 *
 * Returns the count of carryover lines created (used for the wizard
 * banner).
 */
export async function applyCarryoversForRun(currentRunId, currentArByKey) {
  const heldAll = await api.invoices.listOutstandingHeld(currentRunId);
  if (heldAll.length === 0) return 0;

  const carryoverInserts = [];
  const sourceUpdates    = [];

  // Carryover lookups need to use the source RUN's ar_document_format
  // (pinned at upload time) so the key scheme matches what generated
  // this held invoice. Falls back to the source tenant's default for
  // legacy runs that predate the per-run column.
  const allTenants = await api.tenants.list();
  const tenantByIdForCarryover = new Map(allTenants.map(t => [t.id, t]));
  const srcRunIds = Array.from(new Set(heldAll.map(s => s.runId).filter(Boolean)));
  const [currentRun, ...srcRuns] = await Promise.all([
    api.runs.get(currentRunId).catch(() => null),
    ...srcRunIds.map(id => api.runs.get(id).catch(() => null)),
  ]);
  const runByIdForCarryover = new Map(srcRuns.filter(Boolean).map(r => [r.id, r]));

  // Carryovers only flow forward in time. Recalculating an OLDER run must
  // not pull a newer run's held invoices back into it (that happened once:
  // a June recalc "released" July's held invoices into June). Drop any
  // source whose run period isn't strictly before the current run's.
  const held = heldAll.filter(src => {
    const srcRun = runByIdForCarryover.get(src.runId);
    return !!(srcRun?.periodStart && currentRun?.periodStart && srcRun.periodStart < currentRun.periodStart);
  });
  if (held.length === 0) return 0;

  for (const src of held) {
    const srcTenant = tenantByIdForCarryover.get(src.tenantId);
    const srcRun    = runByIdForCarryover.get(src.runId);
    const codes = srcTenant?.branchCodes || {};
    const arDocFormat = srcRun?.arDocumentFormat || srcTenant?.arDocumentFormat;
    const arKey = buildInvoiceKey(src.invoiceNumber, src.branch, codes, arDocFormat);
    if (currentArByKey.has(arKey)) {
      // Still outstanding in the current period — leave it held; we'll
      // pick it up the next month it gets paid.
      continue;
    }
    // It's been paid since the source run ran. Carry it forward.
    const net  = Number(src.net) || 0;
    const rate = Number(src.rate) || 0;
    const releasedCommission = Math.round(net * rate * 100) / 100;
    carryoverInserts.push({
      tenantId:       src.tenantId,
      runId:          currentRunId,
      customerId:     src.customerId,
      repUserId:      src.repUserId,
      invoiceNumber:  src.invoiceNumber,
      branch:         src.branch,
      entity:         src.entity,
      invoiceDate:    src.invoiceDate,
      totalBill:      src.totalBill,
      grossWages:     src.grossWages,
      grossProfit:    src.grossProfit,
      aca:            src.aca,
      sick:           src.sick,
      net:            src.net,
      tier:           src.tier,
      rate:           src.rate,
      commission:     releasedCommission,
      paid:           true,
      outstanding:    0,
      ageDays:        0,
      isCarryover:    true,
      sourceInvoiceId: src.id,
      sourceRunId:    src.runId,
      // Reset rejection / held flags — this is a fresh ledger entry.
      rejected:       false,
      heldForCollection: false,
    });
    sourceUpdates.push(api.invoices.update(src.id, { carriedOver: true }));
  }

  if (carryoverInserts.length === 0) return 0;
  // Bulk insert the new carryover lines, then flip the sources in parallel.
  await api.invoices.insertMany(carryoverInserts);
  await Promise.all(sourceUpdates);
  return carryoverInserts.length;
}

export function formatSize(bytes) {
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024*1024) return (bytes/1024).toFixed(1) + ' KB';
  return (bytes/1024/1024).toFixed(1) + ' MB';
}
export const normKey = s => String(s).toLowerCase().replace(/[\s_\-\.\:]/g,'');
export function pickCol(row, names) {
  const keys = Object.keys(row);
  for (const n of names) {
    const target = normKey(n);
    const k = keys.find(key => normKey(key) === target);
    if (k) return row[k];
  }
  return null;
}
// Build the match key for AR document ↔ txn invoice reconciliation.
//
// Tenants can configure `ar_document_format` on their row:
//   - 'branchInitials+invoiceNumber' (default, e.g. JobSource TSI):
//       AR doc "CR-9171" ↔ txn Carson/9171 → key "CR9171"
//     Branch initials are prepended because txn invoice numbers are
//     scoped by branch and re-used across branches.
//   - 'invoiceNumberOnly' (e.g. AllianceHR):
//       AR doc "JOBS-0284" ↔ txn JOBS-0284 → key "JOBS-0284"
//     Invoice numbers already carry a globally-unique prefix, so no
//     branch concatenation is needed (would in fact break matching).
//
// The `codes` map is the tenant's branchCodes override (Carson → CR)
// for the algorithmic branchInitials fallback.
// normDoc — strip spaces/dashes/underscores + uppercase. Same
// normalization the AR parser applies to its Document no. column, so
// keys on both sides collide.
const normDoc = s => String(s ?? '').replace(/[\s_\-]/g, '').toUpperCase();

// Detect the AR document format from a batch of txn invoice numbers.
// New source systems ship globally-prefixed numbers ("JSCH-0002",
// "JOBS-0284", "AHR-76315") — those already carry the tenant/branch
// prefix baked in, so AR joins on the invoice number alone. Old files
// have bare numbers ("9171") that need branch-initial concatenation.
//
// Heuristic: if a strong majority of numbers start with 2+ letters and
// end in digits, treat the whole run as `invoiceNumberOnly`. Otherwise
// leave the format unset and let the tenant default apply.
export function detectArDocumentFormat(invoiceNumbers) {
  const nums = (invoiceNumbers || [])
    .map(n => String(n ?? '').trim())
    .filter(Boolean);
  if (nums.length < 5) return null; // too small a sample to be confident
  const prefixed = /^[A-Za-z]{2,}[-_ ]?\d+$/;
  const hits = nums.filter(n => prefixed.test(n)).length;
  return hits / nums.length >= 0.8 ? 'invoiceNumberOnly' : null;
}

export function buildInvoiceKey(invoiceNumber, branch, codes, arDocumentFormat) {
  const num = String(invoiceNumber ?? '').trim();
  if (arDocumentFormat === 'invoiceNumberOnly') {
    // AllianceHR-style: invoice number already carries a globally
    // unique prefix (JOBS-0284, JSLH-0002, AHR-76315). Just normalize.
    return normDoc(num);
  }
  // JobSource-style: prepend branch initials to a bare invoice number,
  // then normalize. Handles 'CR-9171' AR ↔ 'Carson/9171' txn.
  return normDoc(branchInitials(branch, codes) + num);
}

// Branch initials used for AR document-number matching.
//
// Most multi-word branches work via "first letter of each word"
// ("Santa Ana" -> "SA", "Huntington Park" -> "HP"). But single-word
// branches break that rule (Carson -> CR, Downey -> DW, Commerce -> CM,
// Chino -> CH) — they need an explicit override that the tenant
// configures.
//
// Pass an optional `codes` map (typically `tenant.branchCodes`) to
// override the algorithm. Lookup is case-insensitive.
export function branchInitials(branch, codes) {
  if (!branch) return '';
  const s = String(branch).trim();
  if (codes && typeof codes === 'object') {
    // Case-insensitive lookup.
    const lower = s.toLowerCase();
    for (const [k, v] of Object.entries(codes)) {
      if (String(k).trim().toLowerCase() === lower && v) return String(v).toUpperCase();
    }
  }
  if (s.length <= 3) return s.toUpperCase();
  return s.split(/[\s,.\-]+/).filter(Boolean).map(w => w[0]).join('').toUpperCase();
}
// Some source systems export money as strings ("$1,141.92" or "(45.00)"
// for negatives). Strip currency/thousands chars and parens-as-negative
// before Number() so the aggregator doesn't silently turn them into 0.
export function num(v) {
  if (v === null || v === undefined || v === '') return 0;
  if (typeof v === 'number') return isFinite(v) ? v : 0;
  let s = String(v).trim();
  const negParens = /^\(.*\)$/.test(s);
  if (negParens) s = s.slice(1, -1);
  s = s.replace(/[$,\s]/g, '');
  const n = Number(s);
  if (!isFinite(n)) return 0;
  return negParens ? -n : n;
}

// Build invoice-level aggregates from raw transaction rows using a tenant's column mapping.
// Accepts an optional `branchCodes` map (typically tenant.branchCodes) so AR
// prefix mapping (Carson -> CR, etc.) is correct.
//
// Two ID concepts:
//   - customerId      = the SUB-SITE / raw ID on this row (e.g. CustomerID)
//   - rootCustomerId  = the PARENT customer ID (e.g. RootCustomerID). If
//                       the tenant's mapping doesn't include rootCustomerId,
//                       we fall back to customerId — preserving the legacy
//                       behavior for source systems that don't have a
//                       root/sub distinction.
// Per aggregated invoice we record both, plus `isParentRow` (true when
// customerId == rootCustomerId — i.e. this row is the "head" of the root
// customer, with the clean unprefixed name).
export function aggregateInvoices(rows, mapping, branchCodes, arDocumentFormat, grossProfitOverride) {
  const m = mapping || {};
  const codes = branchCodes || {};
  const invoices = {};
  // When the tenant sets grossProfitOverride (a decimal, e.g. 0.105),
  // we recompute GP as override × totalBill AFTER aggregation. This
  // exists because JobSource's new source system exports GP without
  // burden deducted (~23% of bill vs the historical ~10.5%), which
  // would double every commission. Rather than re-plumb burden through
  // the commission plan, we normalize GP at parse time.
  const gpOverride = (grossProfitOverride !== null && grossProfitOverride !== undefined && grossProfitOverride !== '')
    ? Number(grossProfitOverride) : null;
  rows.forEach(r => {
    const branch = r[m.invoiceBranch];
    const number = r[m.invoiceNumber];
    if (!branch || number == null) return;
    // Aggregation key uses tenant's ar_document_format so we group rows
    // into invoices the same way AR upload matches them later.
    const key = buildInvoiceKey(number, branch, codes, arDocumentFormat) + '|' + String(number).trim();
    const subId  = m.customerId      ? String(r[m.customerId]      ?? '').trim() : '';
    const rootId = m.rootCustomerId  ? String(r[m.rootCustomerId]  ?? '').trim() : subId;
    const isParentRow = !!subId && !!rootId && subId === rootId;
    if (!invoices[key]) {
      invoices[key] = {
        key,
        invoiceNumber: String(number).trim(),
        branch: String(branch).trim(),
        branchInitials: branchInitials(branch, codes),
        customer: r[m.customerName] || '',
        customerId: subId,
        rootCustomerId: rootId,
        isParentRow,
        entity: r[m.entity] || '',
        invoiceDate: r[m.invoiceDate] || null,
        totalBill: 0, grossWages: 0, grossProfit: 0,
        rowCount: 0,
      };
    }
    const inv = invoices[key];
    inv.totalBill   += num(r[m.totalBill]);
    inv.grossWages  += num(r[m.grossWages]);
    inv.grossProfit += num(r[m.grossProfit]);
    inv.rowCount++;
  });
  const list = Object.values(invoices);
  if (gpOverride !== null && Number.isFinite(gpOverride)) {
    list.forEach(inv => {
      inv.grossProfit = Math.round(inv.totalBill * gpOverride * 100) / 100;
    });
  }
  return list;
}

// Month window (used for period-scope check).
export function inMonth(d, year, month) {
  if (!d) return false;
  const date = d instanceof Date ? d : new Date(d);
  return date.getUTCFullYear() === year && date.getUTCMonth() === (month - 1);
}

// Inclusive-range date check. Both args are ISO date strings ('2026-03-01').
// Returns true if invoiceDate is on or between startISO and endISO. Used
// by the upload parser to scope invoices to whatever period the admin
// picked in step 1 of the wizard.
export function inRange(d, startISO, endISO) {
  if (!d || !startISO || !endISO) return false;
  const date = d instanceof Date ? d : new Date(d);
  const day = Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate());
  const s = new Date(startISO);
  const e = new Date(endISO);
  const sUtc = Date.UTC(s.getUTCFullYear(), s.getUTCMonth(), s.getUTCDate());
  const eUtc = Date.UTC(e.getUTCFullYear(), e.getUTCMonth(), e.getUTCDate());
  return day >= sUtc && day <= eUtc;
}

// First/last day of a YYYY-MM month (returns ISO date strings 'YYYY-MM-DD').
export function monthBounds(yyyyMm) {
  const [y, m] = yyyyMm.split('-').map(Number);
  const first = `${y}-${String(m).padStart(2, '0')}-01`;
  const last  = new Date(Date.UTC(y, m, 0)); // day 0 of next month = last of this month
  const lastISO = `${last.getUTCFullYear()}-${String(last.getUTCMonth() + 1).padStart(2, '0')}-${String(last.getUTCDate()).padStart(2, '0')}`;
  return { start: first, end: lastISO };
}

// "2026-03-15" -> "Mar 15, 2026"
export function fmtDateShort(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
}

// "2026-03" -> "March 2026"
export function fmtMonthLong(yyyyMm) {
  if (!yyyyMm) return '';
  const [y, m] = yyyyMm.split('-').map(Number);
  const d = new Date(Date.UTC(y, m - 1, 1));
  return d.toLocaleDateString('en-US', { month: 'long', year: 'numeric', timeZone: 'UTC' });
}

// ===================== Plan-driven commission helpers =====================

// Map an invoice to a base amount per the plan's base_field, then subtract
// any declared deductions (e.g. ACA, SICK). All deductions multiply some
// invoice column (most commonly grossWages) by a rate.
//
// Important: when the plan's base_field is 'net', deductions are NOT
// applied a second time — `inv.net` is already grossProfit minus ACA + SICK
// from the parser. Applying plan deductions on top would double-subtract.
// For the other base fields (gross_profit, total_bill, gross_wages) the
// deductions are applied as configured.
export function computeBaseFromPlan(inv, plan) {
  const FIELD = {
    gross_profit: 'grossProfit',
    total_bill:   'totalBill',
    gross_wages:  'grossWages',
    net:          'net',
  };
  const baseKey = FIELD[plan.baseField] || 'grossProfit';
  let base = Number(inv[baseKey] || 0);
  // Skip deductions when base is already net — see comment above.
  if (plan.baseField !== 'net') {
    for (const d of (plan.deductions || [])) {
      const fk = FIELD[d.field] || 'grossWages';
      base -= Number(inv[fk] || 0) * Number(d.rate || 0);
    }
  }
  return Math.round(base * 100) / 100;
}

// Walk the plan's tier ladder and pick the first one whose criteria match
// this invoice. Used when plan.tierBasis === 'per_invoice_tenure'.
const YEAR_MS = 365 * 86400000;
export function pickTierForInvoice(inv, customer, plan) {
  const tiers = plan.tiers || [];
  for (const t of tiers) {
    const c = t.criteria || {};
    if (c.type === 'client_tenure_months') {
      const firstInv = customer?.firstInvoice ? new Date(customer.firstInvoice) : null;
      const invDate  = inv.invoiceDate ? new Date(inv.invoiceDate) : null;
      if (!firstInv || !invDate) continue;
      const months = (invDate - firstInv) / (YEAR_MS / 12);
      const min = c.min == null ? -Infinity : Number(c.min);
      const max = c.max == null ?  Infinity : Number(c.max);
      if (months >= min && months < max) return t;
    } else if (c.type === 'none' || !c.type) {
      // Always-match catch-all (used as the last fallback bracket).
      return t;
    } else {
      continue;
    }
  }
  return tiers[tiers.length - 1] || null;
}

// Given a running total before this invoice + the invoice's base amount,
// compute the additional commission earned. Used when plan.tierBasis ===
// 'cumulative_period'. Tiers must have criteria.type === 'cumulative_amount'
// with min/max defining the bracket boundaries.
//
// Example: plan tiers = [
//   { criteria: { type: 'cumulative_amount', min: 0,      max: 50000  }, rate: 0.05 },
//   { criteria: { type: 'cumulative_amount', min: 50000,  max: 100000 }, rate: 0.07 },
//   { criteria: { type: 'cumulative_amount', min: 100000, max: null   }, rate: 0.09 },
// ]
// If running total is 40k and this invoice's base is 30k:
//   slice 0–50k:    10k × 5% = 500
//   slice 50–70k:   20k × 7% = 1400
//   total           1900
// Phase 3 — per-rep minimum-quota gate. Mutates the array of computed
// invoice commissions in place. Returns true if the quota actually
// changed any commission (used for telemetry / banner).
//
// Modes:
//   'none'           → no-op, every dollar earns at the tier rate.
//   'all_or_nothing' → if total base for the rep < minQuota, ZERO out
//                      every invoice's commission for this run.
//   'above_only'     → rep earns commission only on the portion of base
//                      above the minimum. Math: scale every commission
//                      by (totalBase − minQuota) / totalBase. If total
//                      base ≤ minQuota, all commissions go to zero.
//
// The 'above_only' scaling is exact for flat-rate plans and a faithful
// approximation for tiered plans (each invoice's commission is reduced
// in proportion to the rep's overall under-quota fraction). If a buyer
// later wants per-tier exact math, we revisit then — this MVP is honest
// about that in the help copy.
export function applyQuotaToRep(computed, plan) {
  const minQuota = Number(plan.minQuota || 0);
  const mode     = plan.quotaMode || 'none';
  if (minQuota <= 0 || mode === 'none' || computed.length === 0) return false;

  const totalBase = computed.reduce((s, x) => s + (x.base || 0), 0);

  if (mode === 'all_or_nothing') {
    if (totalBase < minQuota) {
      computed.forEach(x => { x.commission = 0; });
      return true;
    }
    return false;
  }

  if (mode === 'above_only') {
    if (totalBase <= minQuota) {
      computed.forEach(x => { x.commission = 0; });
      return true;
    }
    const factor = (totalBase - minQuota) / totalBase;
    computed.forEach(x => {
      x.commission = Math.round((x.commission || 0) * factor * 100) / 100;
    });
    return true;
  }

  return false;
}

export function commissionFromBrackets(prevTotal, additionalBase, tiers) {
  let total = 0;
  let dominantTier = null;
  let largestSlice = 0;
  const start = prevTotal;
  const end   = prevTotal + additionalBase;
  for (const t of (tiers || [])) {
    const c = t.criteria || {};
    if (c.type !== 'cumulative_amount') continue;
    const min = c.min == null ? 0        : Number(c.min);
    const max = c.max == null ? Infinity : Number(c.max);
    const sliceStart = Math.max(start, min);
    const sliceEnd   = Math.min(end,   max);
    if (sliceEnd > sliceStart) {
      const sliceWidth = sliceEnd - sliceStart;
      total += sliceWidth * Number(t.rate || 0);
      if (sliceWidth > largestSlice) { largestSlice = sliceWidth; dominantTier = t; }
    }
  }
  return { commission: Math.round(total * 100) / 100, dominantTier };
}

// ===================== S05 · Upload Txn =====================
export function ScreenUploadTxn({ onNav, wizard, setWizard, tenant }) {
  const [file, setFile] = useStateAdmin(wizard.txnFile);
  const [error, setError] = useStateAdmin(null);
  const [saving, setSaving] = useStateAdmin(false);
  // Local editable copy of the tenant's txn column mapping. We start
  // from what's persisted on the tenant and let the admin fix it inline
  // when required fields aren't mapped. Saving writes back to the tenant.
  const [mapping, setMapping] = useStateAdmin((tenant && tenant.txnMapping) || {});
  const [mappingSaving, setMappingSaving] = useStateAdmin(false);
  React.useEffect(() => {
    setMapping((tenant && tenant.txnMapping) || {});
  }, [tenant?.id]);
  const inputRef = React.useRef(null);

  const handleFile = async (f) => {
    if (!f) return;
    setError(null);
    try {
      const rows = await parseSpreadsheet(f);
      if (rows.length === 0) { setError('File is empty.'); return; }
      // Validate mapping exists in sample
      const sampleKeys = Object.keys(rows[0]);
      const required = ['customerName','invoiceBranch','invoiceNumber','invoiceDate','totalBill','grossWages','grossProfit'];
      const missing = required.filter(k => !mapping[k] || !sampleKeys.includes(mapping[k]));
      // If the tenant has no mapping at all yet, auto-suggest matches from
      // the file's column headers (same heuristic as the new-tenant wizard).
      // The admin can adjust + save below before continuing.
      if (Object.keys(mapping).length === 0 && missing.length > 0) {
        const suggest = {};
        const ALL_FIELDS = ['customerName','invoiceBranch','invoiceNumber','invoiceDate','totalBill','grossWages','grossProfit','entity','customerId','rootCustomerId'];
        ALL_FIELDS.forEach(key => {
          let match;
          if (key === 'rootCustomerId') {
            // Pick the Root* column — the parent customer ID. Used for
            // grouping invoices into root-level customer rows.
            match = sampleKeys.find(c => /^root.*customer.*id$/i.test(c))
                 || sampleKeys.find(c => normKey(c).includes('rootcustomer'))
                 || sampleKeys.find(c => normKey(c).startsWith('root') && normKey(c).includes('id'));
          } else if (key === 'customerId') {
            // The sub-site / raw ID. Avoid Root* matches (those belong to
            // rootCustomerId above).
            match = sampleKeys.find(c => normKey(c) === 'customerid' && !/^root/i.test(c))
                 || sampleKeys.find(c => normKey(c).includes('customerid') && !/^root/i.test(c));
          } else {
            match = sampleKeys.find(c => normKey(c) === normKey(key))
                 || sampleKeys.find(c => normKey(c).includes(normKey(key)));
          }
          if (match) suggest[key] = match;
        });
        setMapping(suggest);
      }
      // Detect the AR-document format from the file itself. If invoice
      // numbers already carry a global prefix (JSCH-0002), the run pins
      // to invoiceNumberOnly; else we fall back to the tenant default.
      // This lets an old run (bare numbers) coexist with a new run
      // (prefixed) on the same tenant without a manual toggle.
      const sampleNums = rows
        .map(r => mapping.invoiceNumber ? r[mapping.invoiceNumber] : null)
        .filter(Boolean)
        .slice(0, 200);
      const detected = detectArDocumentFormat(sampleNums);
      const effectiveFormat = detected || tenant?.arDocumentFormat;
      const invoices = aggregateInvoices(rows, mapping, tenant?.branchCodes, effectiveFormat, tenant?.grossProfitOverride);
      // Scope to the run period the admin set in step 1 (wizard.run.periodStart/End).
      // Falls back to "everything in scope" if for some reason the run wasn't
      // saved with dates (legacy / dev mode).
      const start = wizard?.run?.periodStart;
      const end   = wizard?.run?.periodEnd;
      const inScope = (start && end)
        ? invoices.filter(i => inRange(i.invoiceDate, start, end))
        : invoices;
      const outOfScope = invoices.length - inScope.length;
      const gpTotal = invoices.reduce((a,i) => a + i.grossProfit, 0);
      const billTotal = invoices.reduce((a,i) => a + i.totalBill, 0);
      const entities = Array.from(new Set(invoices.map(i => i.entity).filter(Boolean)));
      const branches = Array.from(new Set(invoices.map(i => i.branch).filter(Boolean)));
      setFile({
        name: f.name, size: formatSize(f.size), rows, invoices,
        rowCount: rows.length, missing, outOfScope, gpTotal, billTotal,
        invoiceCount: invoices.length, inScope: inScope.length,
        entities, branches,
        arDocumentFormat: effectiveFormat,
      });
    } catch (err) { setError('Could not parse file: ' + err.message); }
  };

  // Re-run the validation + preview after a mapping change without making
  // the admin re-pick the file. Uses the rows already in `file.rows`.
  const reaggregate = (newMapping) => {
    if (!file?.rows) return;
    const sampleKeys = Object.keys(file.rows[0] || {});
    const required = ['customerName','invoiceBranch','invoiceNumber','invoiceDate','totalBill','grossWages','grossProfit'];
    const missing = required.filter(k => !newMapping[k] || !sampleKeys.includes(newMapping[k]));
    const sampleNums = file.rows
      .map(r => newMapping.invoiceNumber ? r[newMapping.invoiceNumber] : null)
      .filter(Boolean)
      .slice(0, 200);
    const detected = detectArDocumentFormat(sampleNums);
    const effectiveFormat = detected || tenant?.arDocumentFormat;
    const invoices = aggregateInvoices(file.rows, newMapping, tenant?.branchCodes, effectiveFormat, tenant?.grossProfitOverride);
    const start = wizard?.run?.periodStart;
    const end   = wizard?.run?.periodEnd;
    const inScope = (start && end)
      ? invoices.filter(i => inRange(i.invoiceDate, start, end))
      : invoices;
    const outOfScope = invoices.length - inScope.length;
    const gpTotal   = invoices.reduce((a,i) => a + i.grossProfit, 0);
    const billTotal = invoices.reduce((a,i) => a + i.totalBill, 0);
    const entities  = Array.from(new Set(invoices.map(i => i.entity).filter(Boolean)));
    const branches  = Array.from(new Set(invoices.map(i => i.branch).filter(Boolean)));
    setFile({
      ...file,
      invoices, rowCount: file.rows.length, missing, outOfScope, gpTotal, billTotal,
      invoiceCount: invoices.length, inScope: inScope.length, entities, branches,
      arDocumentFormat: effectiveFormat,
    });
  };

  // Save the current mapping back to the tenant row so it's the default
  // for the next upload too. RLS on tenants update is admin-gated.
  const saveMapping = async () => {
    if (!tenant?.id) return;
    setMappingSaving(true); setError(null);
    try {
      await api.tenants.update(tenant.id, { txnMapping: mapping });
      reaggregate(mapping);
    } catch (err) {
      setError('Could not save mapping: ' + (err.message || err));
    } finally { setMappingSaving(false); }
  };

  // The set of field keys the mapper UI surfaces. Required fields are
  // marked with an asterisk; optional ones (entity, customerId) are
  // valuable but not blocking.
  const MAPPABLE_FIELDS = [
    ['customerName',     'Customer name',                true],
    ['invoiceBranch',    'Invoice branch',               true],
    ['invoiceNumber',    'Invoice number',               true],
    ['invoiceDate',      'Invoice date',                 true],
    ['totalBill',        'Total bill',                   true],
    ['grossWages',       'Gross wages',                  true],
    ['grossProfit',      'Gross profit',                 true],
    ['entity',           'Staffing entity / supplier',   false],
    ['customerId',       'Customer ID (sub-site / raw)', false],
    ['rootCustomerId',   'Root customer ID (parent)',    false],
  ];
  const fileColumns = file?.rows?.[0] ? Object.keys(file.rows[0]) : [];

  return (
    <div className="max-w-[780px] mx-auto">
      <div className="mb-2 text-[12px] text-neutral-500 font-semibold uppercase tracking-[0.06em]">{wizard?.run?.period || 'New run'} · New run</div>
      <h1 className="text-[24px] font-bold text-neutral-900 tracking-[-0.01em] mb-6">Upload the BoldStaff Transaction export</h1>
      <WizardStepper step={2} onStep={() => onNav('s04')}/>

      <input ref={inputRef} type="file" accept=".xlsx,.xls,.csv" style={{display:'none'}}
             onChange={e => handleFile(e.target.files[0])}/>

      {!file ? (
        <Card padding="p-8">
          <div className="border-2 border-dashed border-neutral-300 rounded-lg py-14 flex flex-col items-center justify-center text-center hover:border-primary-700 hover:bg-primary-100/20 transition cursor-pointer"
               onClick={() => inputRef.current && inputRef.current.click()}
               onDragOver={e => e.preventDefault()}
               onDrop={e => { e.preventDefault(); handleFile(e.dataTransfer.files[0]); }}>
            <div className="w-12 h-12 rounded-full bg-primary-100 flex items-center justify-center mb-3">
              <Icon name="upload" size={20} className="text-primary-700"/>
            </div>
            <div className="text-[15px] font-semibold text-neutral-900">Drop your Transaction file here</div>
            <div className="text-[13px] text-neutral-500 mt-1">or click to browse · .xlsx or .csv up to 50 MB</div>
            <div className="mt-4 text-[11px] text-neutral-500">Expected columns: Date · BillToName · BranchCode · TotalBill · GrossWages · GP · InvoiceDoc</div>
          </div>
          {error && <div className="mt-3 text-[12px] text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2">{error}</div>}
        </Card>
      ) : (
        <div className="space-y-4">
          <Card>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-md bg-emerald-50 border border-emerald-200 flex items-center justify-center"><Icon name="file" size={18} className="text-emerald-700"/></div>
                <div>
                  <div className="text-[14px] font-semibold text-neutral-900">{file.name}</div>
                  <div className="text-[12px] text-neutral-500">{file.size} · uploaded just now</div>
                </div>
              </div>
              <Button variant="ghost" size="sm" onClick={() => setFile(null)}>Replace</Button>
            </div>
          </Card>

          <Card>
            <div className="text-[13px] font-semibold text-neutral-900 mb-3">Validation</div>
            <ul className="space-y-2 text-[13px]">
              <ValidationRow ok={file.missing.length === 0} warn={file.missing.length > 0} label={file.missing.length === 0 ? 'All required tenant-mapped columns present' : `Unmapped required fields: ${file.missing.join(', ')}`}/>
              <ValidationRow ok label={`${file.rowCount.toLocaleString()} time-entry rows aggregated into ${file.invoiceCount.toLocaleString()} invoices`}/>
              <ValidationRow ok={file.outOfScope === 0} warn={file.outOfScope > 0} label={file.outOfScope === 0 ? 'All invoices fall within the run period' : `${file.outOfScope} invoices dated outside run period (filtered out)`}/>
              <ValidationRow ok label={`${file.entities.length} staffing entit${file.entities.length === 1 ? 'y' : 'ies'} · ${file.branches.length} branches detected`}/>
            </ul>
          </Card>

          {/* Inline column mapper — shown when the tenant's saved mapping
              doesn't cover every required field, or 0 invoices came out
              of the aggregator (almost always a mapping problem). Saves
              back to public.tenants.txn_mapping for future uploads. */}
          {(file.missing.length > 0 || file.invoiceCount === 0) && (
            <Card className="border-amber-300 bg-amber-50/40">
              <div className="flex items-start justify-between gap-4 mb-3">
                <div>
                  <div className="text-[14px] font-semibold text-neutral-900">Map your file columns</div>
                  <div className="text-[12px] text-neutral-600 mt-1 leading-relaxed max-w-[520px]">
                    This tenant doesn't have a column mapping saved yet, or the saved mapping doesn't match this file.
                    Tell us which column in your export holds each field. We'll save it so future uploads skip this step.
                  </div>
                </div>
              </div>
              <div className="space-y-2">
                {MAPPABLE_FIELDS.map(([key, label, req]) => (
                  <div key={key} className="grid grid-cols-[200px_1fr] items-center gap-3">
                    <div className="text-[13px] text-neutral-700">
                      {label} {req && <span className="text-red-600">*</span>}
                    </div>
                    <select
                      value={mapping[key] || ''}
                      onChange={e => setMapping({ ...mapping, [key]: e.target.value })}
                      disabled={mappingSaving}
                      className="h-9 px-2 text-[13px] rounded-md border border-neutral-300 bg-white focus:outline-none focus:ring-2 focus:ring-primary-200 focus:border-primary-700">
                      <option value="">— not mapped —</option>
                      {fileColumns.map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>
                ))}
              </div>
              <div className="mt-4 flex items-center justify-end gap-2">
                <Button variant="primary" size="sm" icon="check" onClick={saveMapping} disabled={mappingSaving}>
                  {mappingSaving ? 'Saving…' : 'Save mapping & re-validate'}
                </Button>
              </div>
            </Card>
          )}

          <Card>
            <div className="text-[13px] font-semibold text-neutral-900 mb-3">Preview</div>
            <div className="grid grid-cols-4 gap-px bg-neutral-200 rounded-md overflow-hidden border border-neutral-200">
              {[
                ['Rows parsed', file.rowCount.toLocaleString()],
                ['Invoices (in scope)', file.inScope.toLocaleString()],
                ['Total Bill',  '$' + file.billTotal.toLocaleString('en-US', { maximumFractionDigits: 0 })],
                ['GP total',    '$' + file.gpTotal.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })],
              ].map(([k, v]) => (
                <div key={k} className="bg-white p-4">
                  <div className="text-[11px] font-semibold uppercase tracking-[0.06em] text-neutral-500">{k}</div>
                  <div className="mt-1 text-[17px] font-bold font-mono tabular-nums text-neutral-900">{v}</div>
                </div>
              ))}
            </div>
          </Card>
        </div>
      )}

      <div className="mt-5 flex items-center justify-between">
        <Button variant="ghost" onClick={() => onNav('s04')}>Back</Button>
        <Button variant="primary" icon="arrow" disabled={!file || saving} onClick={async () => {
          setError(null);
          setSaving(true);
          try {
            if (!wizard.run?.id) throw new Error('No run in progress. Go back to step 1.');

            // Convert an invoice date into a PostgreSQL-friendly ISO string (YYYY-MM-DD).
            // Handles: Date objects (cellDates: true), Excel serial numbers (fallback),
            // pre-formatted strings, null.
            const toISODate = (v) => {
              if (!v) return null;
              if (v instanceof Date) return v.toISOString().slice(0, 10);
              if (typeof v === 'number') return new Date(Date.UTC(1899, 11, 30) + v * 86400000).toISOString().slice(0, 10);
              // Try parsing string
              const d = new Date(v);
              if (!isNaN(d.getTime())) return d.toISOString().slice(0, 10);
              return null;
            };

            const rates = tenant.rates;
            // Use the actual run period from step 1 — not hardcoded March 2026.
            const periodStart = wizard?.run?.periodStart;
            const periodEnd   = wizard?.run?.periodEnd;
            // Stash the source rootId on each dbRow during the initial map
            // so we can resolve customer IDs in O(N) below instead of doing
            // a 859×859 find() that locks the main thread for several seconds.
            const dbRows = file.invoices
              .filter(i => periodStart && periodEnd
                ? inRange(i.invoiceDate, periodStart, periodEnd)
                : true)
              .map(i => {
                const aca  = Math.round(i.grossWages * rates.aca  * 100) / 100;
                const sick = Math.round(i.grossWages * rates.sick * 100) / 100;
                const net  = Math.round((i.grossProfit - aca - sick) * 100) / 100;
                const tier = 'y2';                // TODO: derive from customer tenure
                const rate = rates[tier];
                const commission = Math.round(net * rate * 100) / 100;
                return {
                  tenantId: tenant.id,
                  runId:    wizard.run.id,
                  invoiceNumber: String(i.invoiceNumber).trim(),
                  branch:   i.branch,
                  entity:   i.entity || null,
                  invoiceDate: toISODate(i.invoiceDate),
                  totalBill:   i.totalBill,
                  grossWages:  i.grossWages,
                  grossProfit: i.grossProfit,
                  aca, sick, net, tier, rate, commission,
                  paid:        false,
                  outstanding: i.totalBill,
                  ageDays:     0,
                  // NOT persisted — used only to attach customer_id below,
                  // then stripped in the api mapper (which only forwards
                  // known columns). Match the same rootId scheme as the
                  // customer-level dedupe above.
                  __rootId: String(i.rootCustomerId || i.customerId || '').trim(),
                };
              });

            console.log(`[upload] Parsed ${file.invoices.length} invoices, ${dbRows.length} in scope, run=${wizard.run.id}`);

            if (dbRows.length === 0) {
              setError(`No invoices matched the run period (${wizard?.run?.period || 'this period'}). Parsed ${file.invoices.length} invoices total; none had invoice dates in that range. Check the InvoiceDate column, or go back to step 1 and adjust the period.`);
              setSaving(false);
              return;
            }

            // ---- Upsert customers from this file, then link invoices ----
            // Build unique customer records keyed on rootId (or name fallback).
            // We also find the earliest invoice_date per customer, to seed first_invoice.
            const toISODateLocal = (v) => {
              if (!v) return null;
              if (v instanceof Date) return v.toISOString().slice(0, 10);
              if (typeof v === 'number') return new Date(Date.UTC(1899, 11, 30) + v * 86400000).toISOString().slice(0, 10);
              const d = new Date(v);
              return isNaN(d.getTime()) ? null : d.toISOString().slice(0, 10);
            };
            // Group by rootCustomerId (parent). Falls back to customerId when
            // the tenant's mapping doesn't make the root/sub distinction.
            // For the display name, prefer the BillToName from a "parent row"
            // (customerId == rootCustomerId) — that's the clean unprefixed
            // root name. Falls back to first encountered BillToName.
            const custMap = new Map();
            file.invoices.forEach(i => {
              if (periodStart && periodEnd && !inRange(i.invoiceDate, periodStart, periodEnd)) return;
              const rootId = String(i.rootCustomerId || i.customerId || i.customer || '').trim();
              if (!rootId) return;
              const iso = toISODateLocal(i.invoiceDate);
              const existing = custMap.get(rootId);
              if (!existing) {
                custMap.set(rootId, {
                  tenantId: tenant.id,
                  name:     i.customer || rootId,
                  rootId,
                  branch:   i.branch || null,
                  firstInvoice: iso,
                  _seenParentName: !!(i.isParentRow && i.customer), // internal marker
                });
              } else {
                if (iso && (!existing.firstInvoice || iso < existing.firstInvoice)) {
                  existing.firstInvoice = iso;
                }
                // Upgrade the name if THIS row is the parent row and we
                // haven't already locked in a parent-row name.
                if (i.isParentRow && !existing._seenParentName && i.customer) {
                  existing.name = i.customer;
                  existing._seenParentName = true;
                }
              }
            });
            // Strip the internal marker before sending to the API.
            // Preserve first_invoice on customers that already exist —
            // it's an anchor for Y1/Y2 tier resolution and represents the
            // contract start date. The current run's earliest invoice
            // date is RARELY actually the customer's first invoice (they
            // may have been billing for years before they showed up in
            // the platform). Use LEAST(existing, new) so we always
            // capture the earliest known date but never push it forward.
            const existingCustomers = await api.customers.list();
            const existingByRootId = new Map(existingCustomers.map(c => [c.rootId, c]));
            const customerRows = [...custMap.values()].map(({ _seenParentName, ...c }) => {
              const existing = existingByRootId.get(c.rootId);
              if (!existing) return c; // new customer — keep the seed date
              const oldFi = existing.firstInvoice;
              const newFi = c.firstInvoice;
              // Always keep the earliest known date across the two.
              const preserved = (oldFi && newFi) ? (oldFi < newFi ? oldFi : newFi)
                              : (oldFi || newFi);
              return { ...c, firstInvoice: preserved };
            });
            console.log(`[upload] Upserting ${customerRows.length} customers (first_invoice preserved on existing)…`);
            const savedCustomers = await api.customers.upsertMany(customerRows);
            const custByRootId = new Map(savedCustomers.map(c => [c.rootId, c]));

            // Attach customer_id to each invoice row before insert. O(N)
            // using the rootId we stashed during the initial map.
            dbRows.forEach(row => {
              const cust = row.__rootId ? custByRootId.get(row.__rootId) : null;
              if (cust) row.customerId = cust.id;
              delete row.__rootId; // strip before sending to API
            });

            const saved = await api.invoices.replaceForRun(wizard.run.id, dbRows);
            console.log(`[upload] Saved ${saved.length} invoices to run ${wizard.run.id}`);
            // Pin the detected AR document format onto the run. Later
            // recalculates use this instead of the tenant default so an
            // old run stays reproducible even after the tenant flips
            // its format going forward.
            let updatedRun = wizard.run;
            if (file.arDocumentFormat && file.arDocumentFormat !== wizard.run?.arDocumentFormat) {
              try {
                updatedRun = await api.runs.update(wizard.run.id, { arDocumentFormat: file.arDocumentFormat });
                console.log(`[upload] Pinned run ${wizard.run.id} to ar_document_format=${file.arDocumentFormat}`);
              } catch (err) {
                console.warn('[upload] Could not pin ar_document_format on run:', err.message);
              }
            }
            setWizard({ ...wizard, run: updatedRun, txnFile: file });
            onNav('s06');
          } catch (err) {
            setError('Could not save invoices: ' + (err.message || err));
          } finally {
            setSaving(false);
          }
        }}>
          {saving ? 'Saving invoices…' : 'Next: Upload aging file'}
        </Button>
      </div>
    </div>
  );
}

export function ValidationRow({ ok, warn, label }) {
  const tone = ok ? 'emerald' : warn ? 'amber' : 'red';
  const bg = { emerald: 'bg-emerald-50 text-emerald-700 border-emerald-200', amber: 'bg-amber-50 text-amber-700 border-amber-200', red: 'bg-red-50 text-red-700 border-red-200' }[tone];
  return (
    <li className="flex items-center gap-2.5">
      <span className={`w-5 h-5 rounded-full border flex items-center justify-center ${bg}`}>
        <Icon name={ok ? 'check' : warn ? 'warn' : 'close'} size={11}/>
      </span>
      <span className="text-neutral-800">{label}</span>
      {!ok && <button className="ml-auto text-[12px] text-primary-700 hover:text-primary-900 font-medium">View</button>}
    </li>
  );
}

// ===================== S06 · Upload Aging =====================
export function ScreenUploadAging({ onNav, wizard, setWizard, tenant }) {
  const [file, setFile] = useStateAdmin(wizard.agingFile);
  const [error, setError] = useStateAdmin(null);
  const [saving, setSaving] = useStateAdmin(false);
  // Same pattern as the txn step: local editable mapping state, auto-suggest
  // when blank, save back to tenant.ar_mapping via the Save button.
  const [mapping, setMapping] = useStateAdmin((tenant && tenant.arMapping) || {});
  const [mappingSaving, setMappingSaving] = useStateAdmin(false);
  React.useEffect(() => {
    setMapping((tenant && tenant.arMapping) || {});
  }, [tenant?.id]);
  const inputRef = React.useRef(null);

  const handleFile = async (f) => {
    if (!f) return;
    setError(null);
    try {
      const rows = await parseARFile(f);
      if (rows.length === 0) { setError('File is empty.'); return; }
      const sampleKeys = Object.keys(rows[0]);
      const required = ['customerName','documentNo','invoiceDate','total'];
      let activeMapping = mapping;
      // If the tenant has no AR mapping yet, auto-suggest from the file's
      // column headers and use the suggestion for this first validation
      // pass too. The admin can edit + save below.
      if (Object.keys(activeMapping).length === 0) {
        const AR_FIELDS = ['customerName','customerId','documentNo','invoiceDate','dueDate','daysAged','total'];
        const suggest = {};
        AR_FIELDS.forEach(key => {
          const match = sampleKeys.find(c => normKey(c) === normKey(key))
                     || sampleKeys.find(c => normKey(c).includes(normKey(key)));
          if (match) suggest[key] = match;
        });
        setMapping(suggest);
        activeMapping = suggest;
      }
      const missing = required.filter(k => !activeMapping[k] || !sampleKeys.includes(activeMapping[k]));

      const cleanNum = v => { const n = Number(String(v ?? '').replace(/[,$\s]/g,'')); return isFinite(n) ? n : 0; };
      const totals = rows.map(r => cleanNum(r[activeMapping.total]));
      const outstanding = totals.reduce((a,b) => a + b, 0);
      const negatives = totals.filter(t => t < 0).length;

      const today = new Date();
      const ages = rows.map(r => {
        const explicit = r[activeMapping.daysAged];
        if (explicit != null && explicit !== '') { const n = Number(explicit); if (isFinite(n)) return n; }
        const d = r[activeMapping.invoiceDate];
        if (!d) return 0;
        const parsed = typeof d === 'number'
          ? new Date(Date.UTC(1899, 11, 30) + d*86400000)
          : new Date(d);
        return Math.max(0, Math.floor((today - parsed) / 86400000));
      });
      const oldest = ages.length ? Math.max(...ages) : 0;

      // Match AR Document no. to the aggregated txn invoices using the
      // tenant's ar_document_format. buildInvoiceKey handles both:
      //   'branchInitials+invoiceNumber' -> "CR" + "9171" (JobSource)
      //   'invoiceNumberOnly'            -> "JOBS-0284"  (AllianceHR)
      // Both sides pass through normDoc so dashes/spaces/case don't fight.
      const txnInvoices = wizard.txnFile?.invoices || [];
      // Prefer the run's pinned format (set at txn upload time via
      // detectArDocumentFormat). Fall back to the tenant default for
      // legacy runs that predate the per-run column.
      const arDocFormat = wizard.run?.arDocumentFormat || tenant?.arDocumentFormat;
      const codes = tenant?.branchCodes || {};
      const txnKeyByDoc = new Map();
      txnInvoices.forEach(inv => txnKeyByDoc.set(
        buildInvoiceKey(inv.invoiceNumber, inv.branch, codes, arDocFormat), inv
      ));
      const normDoc = s => String(s ?? '').replace(/[\s_\-]/g,'').toUpperCase();

      let matched = 0, unmatched = 0;
      const unmatchedSamples = [];
      rows.forEach(r => {
        const doc = normDoc(r[activeMapping.documentNo]);
        const hit = txnKeyByDoc.get(doc) ||
          // try loose match: strip letters, keep digits
          [...txnKeyByDoc.entries()].find(([k]) => k.replace(/\D/g,'') && doc.endsWith(k.replace(/\D/g,'')))?.[1];
        if (hit) matched++;
        else { unmatched++; if (unmatchedSamples.length < 3) unmatchedSamples.push(r[activeMapping.documentNo]); }
      });

      // Build a per-AR-row structure we can apply to the DB invoices table later.
      // Key: normDoc(ar.documentNo). Value: { outstanding, ageDays }.
      const arByKey = new Map();
      rows.forEach((r, idx) => {
        const key = normDoc(r[activeMapping.documentNo]);
        if (!key) return;
        const outstanding = cleanNum(r[activeMapping.total]);
        const ageDays = ages[idx];
        // If multiple AR rows share a key (rare), sum the outstandings.
        const prev = arByKey.get(key);
        arByKey.set(key, {
          outstanding: (prev?.outstanding || 0) + outstanding,
          ageDays: Math.max(prev?.ageDays || 0, ageDays),
        });
      });

      const uniqueCustomers = new Set(rows.map(r => r[activeMapping.customerName]).filter(Boolean)).size;
      setFile({
        name: f.name, size: formatSize(f.size), rows,
        rowCount: rows.length, missing, negatives, outstanding, oldest,
        matched, unmatched, unmatchedSamples,
        hasTxn: txnInvoices.length > 0,
        uniqueCustomers,
        arByKey,   // normDoc → { outstanding, ageDays }
      });
    } catch (err) { setError('Could not parse file: ' + err.message); }
  };

  // Re-run AR validation with an updated mapping (no re-pick required).
  const reaggregateAR = (m) => {
    if (!file?.rows) return;
    const sampleKeys = Object.keys(file.rows[0] || {});
    const required = ['customerName','documentNo','invoiceDate','total'];
    const missing = required.filter(k => !m[k] || !sampleKeys.includes(m[k]));
    const cleanNum = v => { const n = Number(String(v ?? '').replace(/[,$\s]/g,'')); return isFinite(n) ? n : 0; };
    const totals = file.rows.map(r => cleanNum(r[m.total]));
    const outstanding = totals.reduce((a,b) => a + b, 0);
    const negatives = totals.filter(t => t < 0).length;
    const today = new Date();
    const ages = file.rows.map(r => {
      const explicit = r[m.daysAged];
      if (explicit != null && explicit !== '') { const n = Number(explicit); if (isFinite(n)) return n; }
      const d = r[m.invoiceDate];
      if (!d) return 0;
      const parsed = typeof d === 'number'
        ? new Date(Date.UTC(1899, 11, 30) + d*86400000)
        : new Date(d);
      return Math.max(0, Math.floor((today - parsed) / 86400000));
    });
    const oldest = ages.length ? Math.max(...ages) : 0;
    const txnInvoices = wizard.txnFile?.invoices || [];
    const txnKeyByDoc = new Map();
    txnInvoices.forEach(inv => txnKeyByDoc.set(inv.branchInitials + inv.invoiceNumber, inv));
    const normDoc = s => String(s ?? '').replace(/[\s_\-]/g,'').toUpperCase();
    let matched = 0, unmatched = 0;
    const unmatchedSamples = [];
    file.rows.forEach(r => {
      const doc = normDoc(r[m.documentNo]);
      const hit = txnKeyByDoc.get(doc) ||
        [...txnKeyByDoc.entries()].find(([k]) => k.replace(/\D/g,'') && doc.endsWith(k.replace(/\D/g,'')))?.[1];
      if (hit) matched++;
      else { unmatched++; if (unmatchedSamples.length < 3) unmatchedSamples.push(r[m.documentNo]); }
    });
    const arByKey = new Map();
    file.rows.forEach((r, idx) => {
      const key = normDoc(r[m.documentNo]); if (!key) return;
      const outstandingRow = cleanNum(r[m.total]);
      const ageDays = ages[idx];
      const prev = arByKey.get(key);
      arByKey.set(key, {
        outstanding: (prev?.outstanding || 0) + outstandingRow,
        ageDays: Math.max(prev?.ageDays || 0, ageDays),
      });
    });
    const uniqueCustomers = new Set(file.rows.map(r => r[m.customerName]).filter(Boolean)).size;
    setFile({
      ...file, missing, negatives, outstanding, oldest,
      matched, unmatched, unmatchedSamples, uniqueCustomers, arByKey,
    });
  };

  const saveMapping = async () => {
    if (!tenant?.id) return;
    setMappingSaving(true); setError(null);
    try {
      await api.tenants.update(tenant.id, { arMapping: mapping });
      reaggregateAR(mapping);
    } catch (err) {
      setError('Could not save AR mapping: ' + (err.message || err));
    } finally { setMappingSaving(false); }
  };

  const AR_MAPPABLE_FIELDS = [
    ['customerName', 'Customer name',     true],
    ['documentNo',   'Document number',   true],
    ['invoiceDate',  'Invoice date',      true],
    ['total',        'Total outstanding', true],
    ['customerId',   'Customer ID',       false],
    ['dueDate',      'Due date',          false],
    ['daysAged',     'Days aged',         false],
  ];
  const arFileColumns = file?.rows?.[0] ? Object.keys(file.rows[0]) : [];

  return (
    <div className="max-w-[780px] mx-auto">
      <div className="mb-2 text-[12px] text-neutral-500 font-semibold uppercase tracking-[0.06em]">{wizard?.run?.period || 'New run'} · New run</div>
      <h1 className="text-[24px] font-bold text-neutral-900 tracking-[-0.01em] mb-6">Upload the Sage aging export</h1>
      <WizardStepper step={3} onStep={s => onNav(s === 1 ? 's04' : s === 2 ? 's05' : 's06')}/>

      <input ref={inputRef} type="file" accept=".xlsx,.xls,.csv" style={{display:'none'}}
             onChange={e => handleFile(e.target.files[0])}/>

      {!file ? (
        <Card padding="p-8">
          <div className="border-2 border-dashed border-neutral-300 rounded-lg py-14 flex flex-col items-center justify-center text-center hover:border-primary-700 hover:bg-primary-100/20 transition cursor-pointer"
               onClick={() => inputRef.current && inputRef.current.click()}
               onDragOver={e => e.preventDefault()}
               onDrop={e => { e.preventDefault(); handleFile(e.dataTransfer.files[0]); }}>
            <div className="w-12 h-12 rounded-full bg-primary-100 flex items-center justify-center mb-3">
              <Icon name="upload" size={20} className="text-primary-700"/>
            </div>
            <div className="text-[15px] font-semibold text-neutral-900">Drop your Aging file here</div>
            <div className="text-[13px] text-neutral-500 mt-1">Expected columns: Document no. · Total · Date · Customer</div>
          </div>
          {error && <div className="mt-3 text-[12px] text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2">{error}</div>}
        </Card>
      ) : (
        <div className="space-y-4">
          <Card>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-md bg-emerald-50 border border-emerald-200 flex items-center justify-center"><Icon name="file" size={18} className="text-emerald-700"/></div>
                <div>
                  <div className="text-[14px] font-semibold text-neutral-900">{file.name}</div>
                  <div className="text-[12px] text-neutral-500">{file.size} · uploaded just now</div>
                </div>
              </div>
              <Button variant="ghost" size="sm" onClick={() => setFile(null)}>Replace</Button>
            </div>
          </Card>
          <Card>
            <div className="text-[13px] font-semibold text-neutral-900 mb-3">Validation</div>
            <ul className="space-y-2 text-[13px]">
              <ValidationRow ok={file.missing.length === 0} warn={file.missing.length > 0} label={file.missing.length === 0 ? 'All required tenant-mapped columns present' : `Unmapped: ${file.missing.join(', ')}`}/>
              {file.hasTxn
                ? <ValidationRow ok label={`${file.matched.toLocaleString()} invoices matched to Transaction file (by branch initials + invoice #)`}/>
                : <ValidationRow warn label="No Transaction file uploaded — skipping match check"/>}
              {file.hasTxn && file.unmatched > 0 && (
                <ValidationRow warn label={`${file.unmatched.toLocaleString()} aging rows have no match in Transaction${file.unmatchedSamples.length ? ` (e.g. ${file.unmatchedSamples.join(', ')})` : ''}`}/>
              )}
              <ValidationRow ok={file.negatives === 0} warn={file.negatives > 0} label={file.negatives === 0 ? 'No negative totals' : `${file.negatives} negative totals`}/>
            </ul>
          </Card>

          {/* Inline AR column mapper — shown when the saved mapping is
              missing required fields, or when zero AR rows matched the
              txn invoices (almost always a mapping issue). */}
          {(file.missing.length > 0 || (file.hasTxn && file.matched === 0)) && (
            <Card className="border-amber-300 bg-amber-50/40">
              <div className="text-[14px] font-semibold text-neutral-900 mb-1">Map your AR columns</div>
              <div className="text-[12px] text-neutral-600 mb-3 leading-relaxed max-w-[520px]">
                We need to know which column in your AR export holds each field. Save once and we'll
                remember it for future months.
              </div>
              <div className="space-y-2">
                {AR_MAPPABLE_FIELDS.map(([key, label, req]) => (
                  <div key={key} className="grid grid-cols-[200px_1fr] items-center gap-3">
                    <div className="text-[13px] text-neutral-700">
                      {label} {req && <span className="text-red-600">*</span>}
                    </div>
                    <select
                      value={mapping[key] || ''}
                      onChange={e => setMapping({ ...mapping, [key]: e.target.value })}
                      disabled={mappingSaving}
                      className="h-9 px-2 text-[13px] rounded-md border border-neutral-300 bg-white focus:outline-none focus:ring-2 focus:ring-primary-200 focus:border-primary-700">
                      <option value="">— not mapped —</option>
                      {arFileColumns.map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>
                ))}
              </div>
              <div className="mt-4 flex items-center justify-end gap-2">
                <Button variant="primary" size="sm" icon="check" onClick={saveMapping} disabled={mappingSaving}>
                  {mappingSaving ? 'Saving…' : 'Save mapping & re-validate'}
                </Button>
              </div>
            </Card>
          )}
          <Card>
            <div className="text-[13px] font-semibold text-neutral-900 mb-3">Preview</div>
            <div className="grid grid-cols-4 gap-px bg-neutral-200 rounded-md overflow-hidden border border-neutral-200">
              {[
                ['Open rows', file.rowCount.toLocaleString()],
                ['Customers', file.uniqueCustomers.toLocaleString()],
                ['Total outstanding', '$' + file.outstanding.toLocaleString('en-US',{minimumFractionDigits:2,maximumFractionDigits:2})],
                ['Oldest', file.oldest + ' days'],
              ].map(([k, v]) => (
                <div key={k} className="bg-white p-4">
                  <div className="text-[11px] font-semibold uppercase tracking-[0.06em] text-neutral-500">{k}</div>
                  <div className="mt-1 text-[17px] font-bold font-mono tabular-nums text-neutral-900">{v}</div>
                </div>
              ))}
            </div>
          </Card>
        </div>
      )}

      <div className="mt-5 flex items-center justify-between">
        <Button variant="ghost" onClick={() => onNav('s05')}>Back</Button>
        <Button variant="primary" icon="arrow" disabled={!file || saving} onClick={async () => {
          setError(null);
          setSaving(true);
          try {
            if (!wizard.run?.id) throw new Error('No run in progress — restart the wizard from step 1.');
            // Fetch all invoices already saved to this run (from the Txn upload step).
            const runInvoices = await api.invoices.listByRun(wizard.run.id);
            // Build a lookup by branchInitials+invoiceNumber, same key scheme as the parser.
            // Uses the tenant's branch_codes map (Carson -> CR, etc.) for correctness.
            const codes = tenant?.branchCodes || {};
            const arDocFormat = wizard.run?.arDocumentFormat || tenant?.arDocumentFormat;
            const invByKey = new Map();
            runInvoices.forEach(inv => {
              invByKey.set(buildInvoiceKey(inv.invoiceNumber, inv.branch, codes, arDocFormat), inv);
            });

            // Apply AR rows: unpaid + outstanding + age from AR.
            // Anything NOT in AR = paid (it was billed but isn't outstanding anymore).
            const updates = [];
            const updatedIds = new Set();
            for (const [arKey, ar] of file.arByKey) {
              const inv = invByKey.get(arKey);
              if (!inv) continue;
              updates.push(api.invoices.update(inv.id, {
                paid: false,
                outstanding: ar.outstanding,
                ageDays: Math.round(ar.ageDays),
              }));
              updatedIds.add(inv.id);
            }
            // Invoices not matched in AR: mark as paid.
            for (const inv of runInvoices) {
              if (!updatedIds.has(inv.id)) {
                updates.push(api.invoices.update(inv.id, {
                  paid: true,
                  outstanding: 0,
                  ageDays: 0,
                }));
              }
            }

            console.log(`[aging] Applying ${updates.length} invoice updates (${updatedIds.size} unpaid, ${updates.length - updatedIds.size} paid)…`);
            await Promise.all(updates);
            console.log(`[aging] Done.`);

            // ---- Carryovers ----
            // Look at PRIOR-run invoices marked held_for_collection (cash-basis
            // plans, originally unpaid at calc time, not yet carried). For any
            // that are NOT present in the current AR file, they've been paid
            // since the source run ran — create a carryover line in this run.
            const carryoverCount = await applyCarryoversForRun(wizard.run.id, file.arByKey);
            if (carryoverCount > 0) {
              console.log(`[aging] Carried over ${carryoverCount} previously-held invoices from past runs.`);
            }

            setWizard({ ...wizard, agingFile: { ...file, carryoverCount } });
            onNav('s07');
          } catch (err) {
            setError('Could not apply aging: ' + (err.message || err));
          } finally {
            setSaving(false);
          }
        }}>
          {saving ? 'Applying aging…' : 'Next: Assign unassigned clients'}
        </Button>
      </div>
    </div>
  );
}

// Walk every invoice in a run, look up its customer's rep, and write
// rep_user_id + tier + rate + commission back per the customer's plan +
// rep overrides + quota + payment-basis. Used by ScreenAssign's calculate
// button (initial run-through) and by ScreenRunDetail's Recalculate
// button (to pick up customer/rep/plan changes after the run was first
// calculated).
//
// Returns a stats object so the caller can show feedback.
export async function recalculateRun(runId) {
  const [allInvs, allCustomers, allReps, plans] = await Promise.all([
    api.invoices.listByRun(runId),
    api.customers.list(),
    api.users.list(),
    api.commissionPlans.list(),
  ]);
  const defaultPlan = plans.find(p => p.isDefault) || plans[0];
  if (!defaultPlan) throw new Error('No commission plan found. Visit Commission Plans first.');

  const custById = new Map(allCustomers.map(c => [c.id, c]));
  const repById  = new Map(allReps.filter(u => u.role === 'rep').map(r => [r.id, r]));
  const planById = new Map(plans.map(p => [p.id, p]));

  // Group invoices per rep so cumulative-tier reps process as a unit.
  //
  // Rep resolution priority:
  //   1. The invoice's customer's current rep_user_id (preferred — picks up
  //      reassignments made since the last calculate).
  //   2. The invoice's existing rep_user_id stamp (preserves assignments
  //      when the customer was already linked but maybe got unassigned
  //      since, or when a name-match was missed during bulk-assign and
  //      we don't want to silently strip the invoice).
  //   3. None → unassigned, clear back to null.
  //
  // The previous behavior cleared invoices whose customer had no rep,
  // which dropped real commissions if the customer-rep mapping wasn't
  // 100% complete. Now we hold on to the invoice's existing rep stamp.
  const byRep = new Map();
  let unassigned = 0;
  const invoicesToClear = [];
  for (const inv of allInvs) {
    const cust = inv.customerId ? custById.get(inv.customerId) : null;
    const repUserId = cust?.repUserId || inv.repUserId || null;
    if (!repUserId) { unassigned++; invoicesToClear.push(inv); continue; }
    const arr = byRep.get(repUserId) || [];
    arr.push({ inv, cust });
    byRep.set(repUserId, arr);
  }

  const updates = [];
  let perInvoiceCount = 0, cumulativeCount = 0, quotaGated = 0, heldCountTotal = 0;

  // Helper: compute deduction breakdown per the current plan for one invoice.
  // Returns { aca, sick, net, base } using the SAME math as
  // computeBaseFromPlan but exposes the labeled buckets so we can stamp
  // them back to the invoice row (refreshing stale aca/sick/net values
  // that were set at upload time against an older plan config).
  const FIELD_MAP = { gross_profit: 'grossProfit', total_bill: 'totalBill', gross_wages: 'grossWages', net: 'net' };
  const computeBreakdown = (inv, plan) => {
    const baseKey = FIELD_MAP[plan.baseField] || 'grossProfit';
    let aca = 0, sick = 0, totalDed = 0;
    if (plan.baseField !== 'net') {
      for (const d of (plan.deductions || [])) {
        const fk = FIELD_MAP[d.field] || 'grossWages';
        const amt = Math.round(Number(inv[fk] || 0) * Number(d.rate || 0) * 100) / 100;
        totalDed += amt;
        if (/aca/i.test(d.label || ''))       aca  += amt;
        else if (/sick|psl/i.test(d.label || '')) sick += amt;
      }
    }
    const base = Math.round((Number(inv[baseKey] || 0) - totalDed) * 100) / 100;
    const net  = Math.round((Number(inv.grossProfit || 0) - totalDed) * 100) / 100;
    return { aca: Math.round(aca * 100) / 100, sick: Math.round(sick * 100) / 100, net, base };
  };

  // Clear stale rep / commission on invoices with no current customer rep.
  for (const inv of invoicesToClear) {
    updates.push(api.invoices.update(inv.id, {
      repUserId: null, tier: null, rate: null, commission: 0, heldForCollection: false,
    }));
  }

  for (const [repUserId, items] of byRep) {
    const rep = repById.get(repUserId);
    const plan = (rep?.commissionPlanId && planById.get(rep.commissionPlanId)) || defaultPlan;

    // Stage 1 — initial per-invoice commission for this rep.
    const computed = [];
    if (plan.tierBasis === 'cumulative_period') {
      items.sort((a, b) => String(a.inv.invoiceDate || '').localeCompare(String(b.inv.invoiceDate || '')));
      let runningTotal = 0;
      for (const { inv } of items) {
        const breakdown = computeBreakdown(inv, plan);
        const base = breakdown.base;
        const { commission, dominantTier } = commissionFromBrackets(runningTotal, base, plan.tiers);
        const rate = dominantTier ? Number(dominantTier.rate) : 0;
        const tierLabel = dominantTier?.label || 'cumulative';
        computed.push({ invId: inv.id, base, commission, rate, tierKey: tierLabel === 'Y1' ? 'y1' : 'y2', breakdown });
        runningTotal += base;
        cumulativeCount++;
      }
    } else {
      for (const { inv, cust } of items) {
        const tier = pickTierForInvoice(inv, cust, plan);
        if (!tier) { unassigned++; continue; }
        const repOverride = tier.label === 'Y1' ? rep?.y1Rate
                          : tier.label === 'Y2+' ? rep?.y2Rate
                          : null;
        const rate = (repOverride != null) ? repOverride : Number(tier.rate);
        const breakdown = computeBreakdown(inv, plan);
        const base = breakdown.base;
        const commission = Math.round(base * rate * 100) / 100;
        computed.push({ invId: inv.id, base, commission, rate, tierKey: tier.label === 'Y1' ? 'y1' : 'y2', breakdown });
        perInvoiceCount++;
      }
    }

    // Stage 2 — quota gate.
    if (applyQuotaToRep(computed, plan)) quotaGated++;

    // Stage 2b — payment basis (rep override wins over plan).
    const effectiveBasis = rep?.paymentBasis || plan.paymentBasis;
    if (effectiveBasis === 'cash') {
      for (const c of computed) {
        const inv = items.find(it => it.inv.id === c.invId)?.inv;
        const paid = inv ? !!inv.paid : true;
        c.held = !paid;
        if (c.held) { c.commission = 0; heldCountTotal++; }
      }
    }

    for (const c of computed) {
      // Refresh the invoice's deduction columns (aca / sick / net) too —
      // they were stamped at upload time against an older plan, and the
      // drawer / rep dashboard show those values. Without this, Recalculate
      // would fix the commission but leave the breakdown stale.
      updates.push(api.invoices.update(c.invId, {
        repUserId,
        tier: c.tierKey,
        rate: c.rate,
        commission: c.commission,
        heldForCollection: !!c.held,
        aca:  c.breakdown.aca,
        sick: c.breakdown.sick,
        net:  c.breakdown.net,
      }));
    }
  }

  console.log(`[recalculate] ${updates.length} invoice writes: ${perInvoiceCount} per-invoice, ${cumulativeCount} cumulative, ${unassigned} unassigned, ${quotaGated} reps quota-gated, ${heldCountTotal} held (cash)`);
  await Promise.all(updates);

  // ---- Carryover detection (also run during recalculate) ----
  // The AR upload step is the natural trigger, but admins also expect
  // 'Recalculate' to pick up newly-paid prior-run invoices. We
  // reconstruct the current run's effective AR map from its own invoices
  // (anything still marked paid=false is "in AR" from the carryover
  // engine's perspective) and re-run the same logic.
  const freshRunInvs = await api.invoices.listByRun(runId);
  const [allTenantsForCodes, thisRun] = await Promise.all([
    api.tenants.list(),
    api.runs.get(runId).catch(() => null),
  ]);
  const tenantByIdForRecalc = new Map(allTenantsForCodes.map(t => [t.id, t]));
  const thisTenant = tenantByIdForRecalc.get(freshRunInvs[0]?.tenantId);
  const thisTenantCodes = thisTenant?.branchCodes || {};
  const thisArDocFormat = thisRun?.arDocumentFormat || thisTenant?.arDocumentFormat;
  const currentArByKey = new Map();
  for (const inv of freshRunInvs) {
    if (inv.paid) continue;
    const key = buildInvoiceKey(inv.invoiceNumber, inv.branch, thisTenantCodes, thisArDocFormat);
    currentArByKey.set(key, { outstanding: inv.outstanding, ageDays: inv.ageDays });
  }
  let carryoverCount = 0;
  try {
    carryoverCount = await applyCarryoversForRun(runId, currentArByKey);
    if (carryoverCount > 0) console.log(`[recalculate] released ${carryoverCount} carryover invoices from prior held runs.`);
  } catch (err) {
    console.warn('[recalculate] carryover step failed (non-fatal):', err);
  }

  return { totalWrites: updates.length, perInvoiceCount, cumulativeCount, unassigned, quotaGated, heldCountTotal, carryoverCount };
}

// ===================== S07 · Assign customers to reps =====================
// DB-backed: reads customers that appear in the current run's invoices,
// lets the admin pick a sales-rep user for each. On "Calculate run", writes
// rep_user_id back to each invoice so the by-rep views work.
export function ScreenAssign({ onNav, wizard }) {
  const [customers, setCustomers]   = useStateAdmin([]);
  const [invoicesByCust, setInvByC] = useStateAdmin({}); // { customerId: [invoices] }
  const [reps, setReps]             = useStateAdmin([]);
  const [loading, setLoading]       = useStateAdmin(true);
  const [query, setQuery]           = useStateAdmin('');
  const [saving, setSaving]         = useStateAdmin(false);
  const [error, setError]           = useStateAdmin(null);

  useEffectAdmin(() => {
    let alive = true;
    (async () => {
      try {
        // Run's invoices to know which customers apply + their GP.
        const runId = wizard.run?.id;
        if (!runId) { if (alive) setLoading(false); return; }
        const [invs, allCustomers, users] = await Promise.all([
          api.invoices.listByRun(runId),
          api.customers.list(),
          api.users.list(),
        ]);
        if (!alive) return;
        const custIds = new Set(invs.map(i => i.customerId).filter(Boolean));
        const inScope = allCustomers.filter(c => custIds.has(c.id));
        const byCust = {};
        invs.forEach(i => {
          if (!i.customerId) return;
          (byCust[i.customerId] ||= []).push(i);
        });
        setCustomers(inScope);
        setInvByC(byCust);
        setReps(users.filter(u => u.role === 'rep' && u.active));
      } catch (err) {
        setError('Failed to load: ' + err.message);
      } finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, [wizard.run?.id]);

  const filtered = customers.filter(c => c.name.toLowerCase().includes(query.toLowerCase()));
  const resolved = customers.filter(c => c.repUserId).length;
  const remaining = customers.length - resolved;

  // Called when admin picks a rep for a customer — write to DB immediately.
  const assignRep = async (customerId, repUserId) => {
    try {
      const updated = await api.customers.update(customerId, { repUserId: repUserId || null });
      setCustomers(cs => cs.map(c => c.id === customerId ? updated : c));
    } catch (err) {
      setError('Could not assign: ' + err.message);
    }
  };

  // Calculate run — delegates to the shared recalculateRun helper above so
  // the same logic powers both the wizard's "Calculate run" button AND
  // the run-detail "Recalculate" button.
  const calculate = async () => {
    setSaving(true); setError(null);
    try {
      await recalculateRun(wizard.run.id);
      onNav('s08');
    } catch (err) {
      setError('Could not recalculate: ' + err.message);
    } finally { setSaving(false); }
  };

  if (loading) return <div className="p-10 text-center text-neutral-500 text-[13px]">Loading customers…</div>;

  return (
    <div className="max-w-[1100px] mx-auto">
      <div className="mb-2 text-[12px] text-neutral-500 font-semibold uppercase tracking-[0.06em]">{wizard.run?.period || 'Run'} · New run</div>
      <h1 className="text-[24px] font-bold text-neutral-900 tracking-[-0.01em] mb-6">Assign each client to a sales rep</h1>
      <WizardStepper step={4} onStep={s => onNav(s === 1 ? 's04' : s === 2 ? 's05' : s === 3 ? 's06' : 's07')}/>

      {remaining > 0 ? (
        <Banner tone="warn" icon="warn" title={`${remaining} client${remaining === 1 ? '' : 's'} still need a sales rep`}>
          You can skip some — unassigned clients won't show up on any rep's dashboard.
        </Banner>
      ) : (
        <Banner tone="success" icon="check" title="Every client has a rep">You're ready to calculate the run.</Banner>
      )}

      <div className="mt-5 flex items-center gap-3">
        <Input icon="search" value={query} onChange={setQuery} placeholder="Search client name…" className="w-72"/>
        <div className="ml-auto text-[12px] text-neutral-500"><span className="text-neutral-900 font-semibold">{resolved}</span> of {customers.length} assigned</div>
      </div>

      <Card padding="p-0" className="mt-4">
        <table className="w-full text-[13px]">
          <thead>
            <tr className="text-left text-[11px] uppercase tracking-[0.06em] text-neutral-500 bg-canvas/60 border-b border-neutral-200">
              <th className="px-4 py-2.5 font-semibold">Client</th>
              <th className="px-4 py-2.5 font-semibold">Branch</th>
              <th className="px-4 py-2.5 font-semibold text-right">Invoices</th>
              <th className="px-4 py-2.5 font-semibold text-right">GP in this run</th>
              <th className="px-4 py-2.5 font-semibold w-[280px]">Sales rep</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-neutral-200">
            {filtered.length === 0 && (
              <tr><td colSpan={5} className="px-4 py-12 text-center text-[13px] text-neutral-500">
                {customers.length === 0
                  ? 'No customers found in this run. Did the Txn upload step complete?'
                  : 'No results match your search.'}
              </td></tr>
            )}
            {filtered.map(c => {
              const custInvs = invoicesByCust[c.id] || [];
              const gp = custInvs.reduce((a, i) => a + (i.grossProfit || 0), 0);
              return (
                <tr key={c.id} className="hover:bg-primary-100/30">
                  <td className="px-4 py-3">
                    <div className="font-semibold text-neutral-900">{c.name}</div>
                    <div className="text-[11px] text-neutral-500 font-mono">{c.rootId}</div>
                  </td>
                  <td className="px-4 py-3 font-mono text-neutral-700">{c.branch || '—'}</td>
                  <td className="px-4 py-3 text-right font-mono">{custInvs.length}</td>
                  <td className="px-4 py-3 text-right"><Money value={gp}/></td>
                  <td className="px-4 py-3">
                    <Select size="sm" value={c.repUserId || ''} placeholder="Choose rep…"
                      onChange={(v) => assignRep(c.id, v)}
                      options={[
                        { value: '', label: '— Unassigned —' },
                        ...reps.map(r => ({ value: r.id, label: r.name })),
                      ]}/>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </Card>

      {error && <div className="mt-3 text-[12px] text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2">{error}</div>}

      <div className="mt-5 flex items-center justify-between">
        <Button variant="ghost" onClick={() => onNav('s06')}>Back</Button>
        <Button variant="success" icon="arrow" disabled={saving || customers.length === 0} onClick={calculate}>
          {saving ? 'Calculating…' : 'Calculate run'}
        </Button>
      </div>
    </div>
  );
}

